/* engine.js — the planner, worked out here.

   Both planning tabs used to be a question asked of something else: the app
   wrote a file of inputs, a language model read it, and a file of answers came
   back a minute or so later. The answers were good. What was wrong with the
   arrangement is that the app already held every number involved — how long
   this kind of work has really taken, where the hours in a day actually are —
   and was posting them out to have arithmetic done on them. A plan that takes
   a minute is a plan you stop asking for, and a plan you cannot check is one
   you can only obey.

   So the reasoning is here, and it is deterministic. The same state in the
   same minute produces the same bytes, every time, on any machine. Nothing in
   this file is random, nothing is persisted, and nothing writes anywhere: the
   engine is a pure read of the state, the Google sync and the clock.

   The shape of it is two passes over one ledger of days.

   The backward pass takes the latest deadline first and allocates it from the
   latest capacity that will hold it. On one preemptible resource that is
   Jackson's rule: if that arrangement cannot fit the work, no arrangement can.
   That is what earns the right to say *already late* as a fact rather than an
   opinion, and it produces the one sentence a study planner exists to produce
   — given what this costs and the hours you really have, what is the last day
   you can start and still finish.

   The forward pass then places the actual sessions from the earliest slot that
   qualifies, in the order the backward pass says they are critical. Latest
   start is the warning; it is never also the plan, because a schedule with
   zero buffer everywhere cascades on one missed evening.

   Running through both is one rule, and it is the app's own: a measurement and
   a guess must never look alike. Every cost carries the rung of evidence it
   came from and the sentence that justifies it, and weak evidence moves *when*
   something is planned, never *how long* it is claimed to take. Nothing here
   multiplies an estimate by an invented safety factor. Where there is nothing
   measured, the honest answer is to say so and to schedule it early enough
   that being wrong is survivable.                                            */

const StudyHubEngine = (() => {
  'use strict';

  /* Bumped only when the block-id scheme (§5.7) changes. app.js's id sweep
     uses it to decide whether the moves and misses keyed on those ids are
     still meaningful after an upgrade. */
  const VERSION = '1';

  /* ─────────────────── the host ─────────────────── */

  /* Classic scripts share one global scope, so the bodies below could simply
     reach for app.js's `S` and `historyStats` and resolve them at call time.
     That works in a browser and is untestable anywhere else. Taking the whole
     world through one explicit object instead makes every dependency visible
     in one list, removes the load-order question, and lets the whole of this
     file be driven from node against a state built by hand. */

  const BINDINGS = [
    'state', 'sync', 'now',
    'DAY_MS', 'startOfDay', 'addDays', 'startOfWeek', 'isoDay', 'fromIsoDay',
    'hhmmToMin', 'minToHhmm', 'pad', 'clamp', 'fmtDay', 'fmtDurMin', 'fmtMinOfDay',
    'courseName', 'courseById',
    'wkKind', 'wkMode', 'seriesKind', 'quantityAttr', 'choiceAttrs',
    'ltKind', 'ltBand',
    'taskShape', 'median',
    'elapsedSec', 'estBaseline', 'wasStarted',
    'deadlineById', 'tasksForDeadline',
    'occurrences', 'instanceOf',
    'standingMultiple', 'historyStats', 'rateStats',
  ];

  /* Null until install() runs, and every entry point below refuses to work
     before that. Typed as the host it will hold rather than as null, so a call
     to a member that does not exist is a mistake the checker catches rather
     than an undefined the planner discovers at render time.
     @type {EngineHost} */
  let H = /** @type {any} */ (null);

  /* A missing binding is fatal and must be fatal at boot. The alternative is a
     planner that silently produces an empty term because one helper was
     renamed, which is the worst failure this app could have: confident,
     instant, and about something the user would have to know the codebase to
     doubt. */
  function install(host) {
    const h = host && typeof host === 'object' ? host : {};
    for (let i = 0; i < BINDINGS.length; i++) {
      const name = BINDINGS[i];
      if (h[name] === undefined || h[name] === null) throw new Error(`engine: missing host binding ${name}`);
    }
    H = h;
    LENS = null;
    LENS_MINUTE = -1;
    return undefined;
  }

  /* ─────────────────── arithmetic that has nowhere else to live ─────────────────── */

  const MS_MIN = 60000;
  const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const num = (v, fallback) => (typeof v === 'number' && isFinite(v) ? v : fallback);
  const list = (v) => (Array.isArray(v) ? v : []);
  const str = (v) => (typeof v === 'string' ? v : (v == null ? '' : String(v)));
  const cmpStr = (a, b) => (a < b ? -1 : a > b ? 1 : 0);

  const roundTo5 = (n) => Math.max(5, Math.round(n / 5) * 5);
  const ceilTo5 = (n) => Math.ceil(n / 5) * 5;

  /* Nearest-rank, so at n = 2 it is the worse of the two and at n = 5 the
     fourth. median() exists in app.js so that one catastrophic all-nighter
     cannot drag an estimate; this exists so that the all-nighter is not
     forgotten either. It is never used to schedule — only to say what a week
     looks like if the work runs long. */
  function p80(nums) {
    if (!nums || !nums.length) return 0;
    const s = nums.slice().sort((a, b) => a - b);
    return s[Math.ceil(0.8 * s.length) - 1];
  }

  const minOfDay = (ms) => { const d = new Date(ms); return d.getHours() * 60 + d.getMinutes(); };

  /* Two views of one moment: take the earlier of whichever exist. A series
     instance whose template carries a deadline has exactly this problem. */
  function earliest(a, b) {
    const xs = [a, b].filter((v) => typeof v === 'number' && isFinite(v));
    return xs.length ? Math.min.apply(null, xs) : null;
  }

  /* A helper handed in by the host may throw on a state row the app would
     never have written but a corrupted localStorage might. One bad row is not
     a reason to blank both planning tabs. */
  function attempt(fn, fallback) {
    try { return fn(); } catch (err) { return fallback; }
  }

  /* Wall-clock for the `ms` figure only. The planning clock arrives through
     H.now() and nowhere else, so that a test can freeze it. */
  const tick = () => (typeof performance !== 'undefined' && performance && typeof performance.now === 'function'
    ? performance.now() : 0);

  /* Ids that survive a round trip through a CSS selector and a data attribute,
     and that a human can read in a bug report. */
  const idSafe = (s) => String(s).replace(/[^a-z0-9:_.-]/g, '-');

  /* FNV-1a, 32-bit. Content-derived ids are the whole reason a dismissal keeps
     holding across a recompute; a random id would re-offer every syllabus date
     the user has already refused. */
  function fnv1a32(input) {
    const s = String(input);
    let h = 0x811c9dc5;
    for (let i = 0; i < s.length; i++) {
      const c = s.charCodeAt(i);
      h = Math.imul(h ^ (c & 0xff), 0x01000193) >>> 0;
      if (c > 0xff) h = Math.imul(h ^ ((c >>> 8) & 0xff), 0x01000193) >>> 0;
    }
    return h >>> 0;
  }

  /* ─────────────────── §2 the cost of a thing ─────────────────── */

  /* Both statistics helpers rebuild a Map over every task in the state, and
     the old predictMin() called one of them per item per day of the grid. A
     solver that priced every demand in every candidate slot the same way would
     be O(demands × slots × tasks). So the evidence is gathered once per run
     and passed down. */

  /** @type {any} */ let LENS = null;
  let LENS_MINUTE = -1;

  function buildLens() {
    const S = H.state() || {};
    const lens = {
      history: attempt(() => list(H.historyStats()), []),
      rates: attempt(() => list(H.rateStats()), []),
      mult: attempt(() => H.standingMultiple(), null),
      multP80: null,
      missSlots: new Map(),
    };

    const mults = [];
    list(S.tasks).forEach((t) => {
      if (t && t.done && typeof t.multiple === 'number' && isFinite(t.multiple)) mults.push(t.multiple);
    });
    lens.multP80 = mults.length ? p80(mults) : null;

    /* Bucketed by weekday and clock hour, which is the same grouping the
       misses list already shows: three missed Friday eight-o'clocks is one
       fact about a life, not three. */
    list((S.weekly || {}).misses).forEach((m) => {
      if (!m || !m.day) return;
      const at = H.hhmmToMin(m.time || m.start);
      if (at == null) return;
      const dayMs = H.fromIsoDay(m.day);
      if (dayMs == null) return;
      const key = `${new Date(dayMs).getDay()}|${Math.floor(at / 60)}`;
      lens.missSlots.set(key, (lens.missSlots.get(key) || 0) + 1);
    });

    return lens;
  }

  /** Rebuild, whatever the clock says, and stamp it with the minute it belongs to. */
  function freshLens(minute) {
    LENS = buildLens();
    LENS_MINUTE = typeof minute === 'number' ? minute : Math.floor(H.now() / MS_MIN);
    return LENS;
  }

  /* The lens this minute is entitled to. The cache exists for one caller only:
     price() is reached per item *per day* from the week grid, and rebuilding
     the history and rate tables on each of those would be the O(items × days ×
     |tasks|) blowup §2.4 was written to forbid.

     A recompute run does not take that bargain — it calls freshLens() instead.
     app.js re-runs the engine the moment anything the planners can see
     changes, precisely so that finishing a task shows up straight away, and a
     lens held over from earlier in the same minute would quietly undo that:
     the plan would be rebuilt around measurements taken before the task the
     user had just finished. One rebuild per run is what §2.4 budgets for
     anyway, so the freshness is free. */
  function currentLens(minute) {
    const m = typeof minute === 'number' ? minute : Math.floor(H.now() / MS_MIN);
    if (LENS && LENS_MINUTE === m) return LENS;
    return freshLens(m);
  }

  /* A weekly item says it is a `pset`; a series template says it is a `pset`
     too, in a different vocabulary. Without this table the same problem set
     typed into the week tab and generated by the series drawer would be priced
     out of two different buckets and quote two different numbers. */
  const WK_TO_SERIES = { pset: 'pset', reading: 'reading-post', post: 'reading-post' };

  /* The ladder. Four rungs, each a weaker claim than the one above it, and the
     basis sentence is returned with the number because the number alone is not
     the whole of what is known.

     No rung adds a safety buffer. Rungs one to three are medians of measured
     minutes, so the overrun is already inside them; rung four has nothing
     behind it at all and the honest response to that is to say so, not to
     multiply by an invented 1.3. */
  function priceOf(d, lens) {
    if (d.mode === 'fixed') {
      const m = Math.max(0, Math.round(num(d.durMin, 0) || 60));
      return { min: m, high: m, rung: 'fixed', n: 0, basis: 'fixed' };
    }

    const est = Math.max(0, Math.round(num(d.estMin, 0)));
    const kind = d.seriesKind || null;

    /* Rung 1 — a rate. The strongest thing the app can know: this many pages
       took this long, per page, in this course. */
    if (kind) {
      const q = attempt(() => H.quantityAttr(kind), null);
      const qty = q && d.attrs ? +d.attrs[q.key] || 0 : 0;
      if (q && qty > 0) {
        const facet = attempt(() => H.choiceAttrs(kind).map((a) => d.attrs[a.key]).filter(Boolean).join('/'), '');
        const exact = lens.rates.find((s) => s.kind === kind && s.facet === facet && s.courseId === (d.courseId || ''));
        const anyCourse = lens.rates.find((s) => s.kind === kind && s.facet === facet);
        const hit = [exact, anyCourse].find((s) => s && s.n >= 2);
        if (hit) {
          const min = Math.max(1, Math.round(qty * hit.medianMinPerUnit));
          const high = Math.max(min, Math.round(qty * num(hit.p80MinPerUnit, hit.medianMinPerUnit)));
          const label = facet ? `${facet} ` : '';
          return {
            min,
            high,
            rung: 'rate',
            n: hit.n,
            basis: `${qty} ${qty === 1 ? q.unit : q.plural} × ${hit.medianMinPerUnit} min/${q.unit}`
              + ` — from ${hit.n} finished ${label}${H.seriesKind(kind).label.toLowerCase()}s`
              + (exact && exact.n >= 2 ? ` in ${hit.group}` : ''),
          };
        }
      }
    }

    /* Rung 2 — the median duration of this shape of work. `plain` series carry
       no shape by design, and a shape nobody has finished twice is no evidence
       at all, so both fall straight through. */
    const shape = kind ? attempt(() => H.seriesKind(kind).shape, '') : attempt(() => H.taskShape(d.title), '');
    if (shape) {
      const group = attempt(() => H.courseName(d.courseId), '');
      const exact = lens.history.find((s) => s.shape === shape && s.group === group);
      const anyCourse = lens.history.find((s) => s.shape === shape);
      const hit = [exact, anyCourse].find((s) => s && s.n >= 2);
      if (hit) {
        const min = Math.max(0, Math.round(hit.medianActualMin));
        return {
          min,
          high: Math.max(min, Math.round(num(hit.p80ActualMin, min))),
          rung: 'median',
          n: hit.n,
          basis: `${hit.n} finished ${hit.shape}${hit.n === 1 ? '' : 's'}`
            + (exact && exact.n >= 2 ? ` in ${hit.group}` : '')
            + ' — not yet measured per unit',
        };
      }
    }

    /* Rung 3 — the user's own estimate, corrected by how wrong her estimates
       have been on average. An average over unlike work, which is why it sits
       this low. */
    if (lens.mult) {
      const min = Math.round(est * lens.mult);
      return {
        min,
        high: Math.max(min, Math.round(est * num(lens.multP80, lens.mult))),
        rung: 'multiple',
        n: 0,
        basis: `your estimate through your ${lens.mult.toFixed(2)}× multiple`,
      };
    }

    /* Rung 4 — the estimate, untouched. The two columns being equal is not an
       omission; it is the signal. Nothing is measured, so no range may be
       claimed. */
    return { min: est, high: est, rung: 'estimate', n: 0, basis: 'your estimate — nothing measured yet' };
  }

  /** The public thin wrapper: the same ladder, from the week list, the item
      editor and the horizon chip, so no screen can quote a different number
      for one problem set. */
  function price(item) {
    const it = item && typeof item === 'object' ? item : {};
    if (!H) return { min: Math.max(0, num(it.estMin, 0)), high: Math.max(0, num(it.estMin, 0)), rung: 'estimate', n: 0, basis: '' };
    const seriesKindKey = it.seriesKind || (it.kind ? (WK_TO_SERIES[it.kind] || null) : null);
    return priceOf({
      title: it.title,
      courseId: it.courseId,
      estMin: it.estMin,
      durMin: it.durMin,
      seriesKind: seriesKindKey,
      attrs: it.attrs || null,
      mode: it.mode === 'fixed' ? 'fixed' : 'work',
    }, currentLens());
  }

  /* ─────────────────── §4 capacity, by subtraction ─────────────────── */

  /* Where the hours are is not a judgement call, and the app holds every input
     it needs to work it out. What follows is subtraction, and the only care it
     takes is that the things being subtracted are made disjoint first: one
     lecture can arrive three times over — as a weekly item, as an event, and
     as a Google row — and a sum over overlapping intervals removes six hours
     from a day that only lost two. */

  function mergeIntervals(intervals) {
    const xs = intervals.slice().sort((a, b) => (a.from - b.from) || (a.to - b.to));
    const out = [];
    xs.forEach((iv) => {
      if (!(iv.to > iv.from)) return;
      const cur = out[out.length - 1];
      if (cur && iv.from <= cur.to) cur.to = Math.max(cur.to, iv.to);
      else out.push({ from: iv.from, to: iv.to });
    });
    return out;
  }

  /** The holes a day has left, once the merged busy runs are taken out of the
      declared window. The cursor walk is freeGaps()'s, lifted so it works on
      any day of the horizon rather than on an index within this week. */
  function gapsIn(busy, dayStart, dayEnd) {
    const gaps = [];
    let cursor = dayStart;
    busy.forEach(({ from, to }) => {
      if (from > cursor) gaps.push({ from: cursor, to: Math.min(from, dayEnd) });
      cursor = Math.max(cursor, to);
    });
    if (cursor < dayEnd) gaps.push({ from: cursor, to: dayEnd });
    return gaps.filter((g) => g.to > g.from);
  }

  function buildCalendar(C) {
    const S = C.S;
    const wk = S.weekly || {};
    const dayStart = H.clamp(num(wk.dayStart, 480), 0, 1380);
    const dayEnd = H.clamp(num(wk.dayEnd, 1320), dayStart + 60, 1440);
    C.dayStart = dayStart;
    C.dayEnd = dayEnd;

    /* How long the horizon is, by horizonWeeks()'s own rule, so the gauge and
       the plan never draw a different number of rows. What stretches it is
       anything already dated: an obligation you cannot see is one you forget. */
    let furthest = 0;
    list(S.tasks).forEach((t) => {
      if (!t || t.done) return;
      const dl = t.deadlineId ? attempt(() => H.deadlineById(t.deadlineId), null) : null;
      const due = earliest(t.series ? t.series.due : null, dl ? dl.due : null);
      if (due) furthest = Math.max(furthest, due);
    });
    list(S.deadlines).forEach((d) => { if (d && d.due) furthest = Math.max(furthest, d.due); });
    list((S.longterm || {}).entries).forEach((e) => { if (e && e.end) furthest = Math.max(furthest, e.end); });
    list(wk.items).forEach((it) => {
      if (!it || it.recur !== 'once') return;
      const ms = H.fromIsoDay(it.date);
      if (ms) furthest = Math.max(furthest, ms);
    });

    const needed = furthest ? Math.ceil((furthest - C.weekStartMs) / (7 * H.DAY_MS)) + 1 : 0;
    C.weekCount = H.clamp(Math.max(num((S.longterm || {}).weeks, 16), Math.min(needed, 104)), 1, 104);
    C.dayCount = C.weekCount * 7;

    /* Every boundary is asked of the calendar rather than counted in
       milliseconds. A day is not always 86,400,000 milliseconds long, and a
       sixteen-week term crosses the night that proves it: stride arithmetic
       drifts an hour, the drifted midnight reads as the previous day, and from
       that Sunday on every weekday in the ledger is wrong by one. */
    const cal = [];
    const idxByIso = new Map();
    let ms = C.weekStartMs;
    for (let i = 0; i < C.dayCount; i++) {
      const iso = H.isoDay(ms);
      cal.push({
        i, dayMs: ms, iso, jsDow: new Date(ms).getDay(),
        label: null,           // filled in on demand, see dayLabel()
        gaps: [], freeMin: 0, leftMin: 0, isProtected: false, protectedBy: '',
      });
      idxByIso.set(iso, i);
      ms = H.startOfDay(H.addDays(new Date(ms), 1)).getTime();
    }
    C.cal = cal;
    C.idxByIso = idxByIso;
    C.horizonEndMs = ms - 1;
    C.todayIdx = idxByIso.has(H.isoDay(C.NOW)) ? idxByIso.get(H.isoDay(C.NOW)) : 0;

    /** @type {Interval[][]} one bucket of busy intervals per day */
    const busy = cal.map(() => /** @type {Interval[]} */ ([]));
    const put = (i, from, to, what) => {
      if (i < 0 || i >= cal.length) return;
      const a = H.clamp(Math.round(from), 0, 1440);
      const b = H.clamp(Math.round(to), 0, 1440);
      if (b > a) busy[i].push({ from: a, to: b, what: what || '' });
    };

    /* 1 — the pinned half of the week: lectures, sections, shifts, clubs. */
    list(wk.items).forEach((it) => {
      attempt(() => {
        if (!it || H.wkMode(it.kind) !== 'fixed') return;
        const at = H.hhmmToMin(it.time);
        if (at == null) return;
        const dur = num(it.durMin, 0) || 60;
        if (it.recur === 'once') {
          const ms2 = H.fromIsoDay(it.date);
          if (ms2 != null) put(dayIndexOf(C, ms2), at, at + dur, it.title);
          return;
        }
        const days = list(it.days);
        if (!days.length) return;
        for (let i = 0; i < cal.length; i++) if (days.includes(cal[i].jsDow)) put(i, at, at + dur, it.title);
      }, null);
    });

    /* 2 and 4 — studyHub's own calendar, and anything already on a clock. A
       task with a start is not demand: it is an appointment with itself. */
    const timed = [];
    list(S.events).forEach((ev) => { if (ev && typeof ev.start === 'number') timed.push(ev); });
    list(S.tasks).forEach((t) => {
      if (t && !t.done && typeof t.start === 'number') timed.push(t);
    });

    /* 3 — and Google's. A lecture that only ever existed in a Google calendar
       is still an hour you are not free; while that only misplaced a suggestion
       it was a small wrong, and under a planner that reasons about capacity it
       becomes a start-by date that is wrong by a day. All-day rows are left out
       deliberately: an all-day entry is a label on a day, not six hours of it. */
    list(C.SY.events).forEach((e) => {
      if (!e || e.allDay) return;
      const start = new Date(e.start).getTime();
      if (!isFinite(start)) return;
      const end = e.end ? new Date(e.end).getTime() : start + 3600000;
      timed.push({ start, end: isFinite(end) ? end : start + 3600000, title: e.title || '(no title)' });
    });

    timed.forEach((it) => {
      attempt(() => {
        const i = dayIndexOf(C, it.start);
        if (i < 0 || i >= cal.length) return;
        const s = new Date(it.start);
        const from = s.getHours() * 60 + s.getMinutes();
        const endMs = num(it.end, 0) || (it.start + (num(it.estMin, 0) || 60) * MS_MIN);
        put(i, from, from + Math.round((endMs - it.start) / MS_MIN), it.title);
      }, null);
    });

    /* 5 — where the user moved a block to. Her own placement is senior to the
       solver's and is booked before the solver runs. A missed block is
       deliberately *not* booked: the slot is dead, not spent. */
    const moved = wk.moved && typeof wk.moved === 'object' ? wk.moved : {};
    Object.keys(moved).forEach((id) => {
      attempt(() => {
        const mv = moved[id];
        if (!mv || !mv.day) return;
        const i = C.idxByIso.has(mv.day) ? C.idxByIso.get(mv.day) : -1;
        const from = H.hhmmToMin(mv.start);
        const to = H.hhmmToMin(mv.end);
        if (i < 0 || from == null || to == null) return;
        put(i, from, to, 'moved by you');
      }, null);
    });

    /* A protected stretch is not capacity at all — the horizon's own words for
       a trip are that the planner should work around it rather than into it. */
    list((S.longterm || {}).entries).forEach((e) => {
      if (!e || !e.protect || !e.end) return;
      const from = typeof e.start === 'number' ? e.start : e.end;
      cal.forEach((day, i) => {
        const next = i + 1 < cal.length ? cal[i + 1].dayMs : C.horizonEndMs + 1;
        if (day.dayMs <= e.end && next > from) {
          day.isProtected = true;
          if (!day.protectedBy) day.protectedBy = e.title || 'protected';
        }
      });
    });

    const floorToday = Math.ceil((minOfDay(C.NOW) + 15) / 15) * 15;
    cal.forEach((day) => {
      if (day.i < C.todayIdx || day.isProtected) return;      // gaps stay empty
      let gaps = gapsIn(mergeIntervals(busy[day.i]), dayStart, dayEnd);
      if (day.i === C.todayIdx) {
        /* Nobody plans backwards into the ten minutes just gone. The quarter
           hour is alternativesFor()'s own grain, so a suggestion and a plan
           agree about when "now" starts. */
        gaps = gaps.map((g) => ({ from: Math.max(g.from, floorToday), to: g.to })).filter((g) => g.to > g.from);
      }
      day.gaps = gaps;
      day.freeMin = gaps.reduce((a, g) => a + (g.to - g.from), 0);
      day.leftMin = day.freeMin;
    });
  }

  /** Which day of the horizon a moment belongs to. Walked through the ISO map
      rather than divided, for the same reason the ledger is built that way. */
  function dayIndexOf(C, ms) {
    if (typeof ms !== 'number' || !isFinite(ms)) return -1;
    const iso = H.isoDay(ms);
    if (C.idxByIso.has(iso)) return C.idxByIso.get(iso);
    return ms < C.cal[0].dayMs ? -1 : C.dayCount;
  }

  /* fmtDay goes through toLocaleDateString, which is by a wide margin the most
     expensive thing either file calls — and the backward pass would otherwise
     ask for the same handful of labels once per obligation per day walked. So a
     day formats itself the first time it is asked and remembers the answer. A
     term of sixteen weeks that nothing ever mentions formats nothing at all. */
  function dayLabel(C, i) {
    const day = C.cal[i];
    if (day.label == null) day.label = H.fmtDay(day.dayMs);
    return day.label;
  }

  /** The label for a moment, whether or not the horizon covers its day. */
  function dayLabelOf(C, ms) {
    const i = dayIndexOf(C, ms);
    return i >= 0 && i < C.dayCount ? dayLabel(C, i) : H.fmtDay(ms);
  }

  /** How much of a day lies before a moment in it. */
  function capacityBefore(day, minute) {
    let n = 0;
    day.gaps.forEach((g) => { n += Math.max(0, Math.min(g.to, minute) - g.from); });
    return n;
  }

  /* ─────────────────── §3 what is being asked of the week ─────────────────── */

  /* Every source of demand the app has, claimed in order of how much is known
     about it. A key claimed by an earlier source is never claimed again, which
     is what makes a materialised series instance and its virtual twin collapse
     into one thing without any matching at all. */

  function newDemand(C, o) {
    const priced = o.priced || priceOf({
      title: o.title, courseId: o.courseId, estMin: o.estMin, durMin: o.durMin,
      seriesKind: o.seriesKind || null, attrs: o.attrs || null,
      mode: o.pricingMode || 'work',
    }, C.lens);

    const loggedMin = Math.max(0, num(o.loggedMin, 0));
    const min = Math.max(0, Math.round(num(priced.min, 0)));
    const high = Math.max(min, Math.round(num(priced.high, min)));
    const owedMin = ceilTo5(Math.max(0, min - loggedMin));

    const kindKey = o.kindKey || '';
    let splittable = true;
    if (o.origin === 'habit' || o.origin === 'deadline') splittable = false;
    else if (o.origin === 'entry' && kindKey === 'event') splittable = false;
    else if (o.origin === 'series' && attempt(() => H.seriesKind(o.seriesKind).key, '') === 'plain'
      && num(o.estMin, 0) <= C.MAX_SESSION) splittable = false;
    else if (owedMin <= C.MIN_SESSION) splittable = false;

    return {
      key: o.key,
      origin: o.origin,
      title: o.title || 'Untitled',
      /* Worked out once. taskShape() is seven regular expressions over a
         string and the pairings ask for it twice per demand; on a full term
         that is a thousand passes over the same dozen titles. */
      shapeKey: attempt(() => H.taskShape(o.title), ''),
      courseId: o.courseId || '',
      mode: o.mode || 'work',
      kindKey,
      seriesKind: o.seriesKind || null,
      dueMs: typeof o.dueMs === 'number' && isFinite(o.dueMs) ? o.dueMs : null,
      releaseMs: Math.max(C.NOW, num(o.releaseMs, C.NOW)),
      min, high, rung: priced.rung, n: priced.n, basis: priced.basis,
      loggedMin, owedMin, splittable,
      itemId: o.itemId || null,
      entryId: o.entryId || null,
      refs: o.refs || {},
      merged: [],
      pinned: !!o.pinned,
      pinnedAt: o.pinnedAt || null,
      absorbed: false,
      absorbedBy: null,
      /* filled in by the two passes */
      claims: [], capacityWalk: [], unplacedMin: 0, criticalDayIdx: 0, latestStartMs: null,
      slackMin: 0, slackDays: 0, sharedWith: 0, freeBeforeDue: 0, overdue: false,
      spanWholeMin: num(o.spanWholeMin, 0),
      spanParts: num(o.spanParts, 0),
      sessionLens: [], sessionsLeft: 0, weekOwedMin: 0, committedMin: 0, spillMin: 0, shortfallMin: 0,
      placed: [], ruled: false, verdict: null, gates: [], trace: null,
    };
  }

  function claim(C, d) {
    if (!d || !d.key || C.byKey.has(d.key)) return null;
    C.byKey.set(d.key, d);
    C.demands.push(d);
    return d;
  }

  function buildInventory(C) {
    const S = C.S;
    const wk = S.weekly || {};
    C.byKey = new Map();
    C.demands = [];
    C.absorbed = [];

    /* 1 — open tasks. The only thing the app has ever timed, so they outrank
       everything: whatever else describes this evening, the task is the copy
       with the measurements on it. */
    list(S.tasks).forEach((t) => {
      attempt(() => {
        if (!t || t.done) return;
        const ser = t.series && t.series.templateId ? t.series : null;
        if (!ser && !t.id) return;
        const dl = t.deadlineId ? H.deadlineById(t.deadlineId) : null;
        claim(C, newDemand(C, {
          key: ser ? `series:${ser.templateId}:${ser.occ}` : `task:${t.id}`,
          origin: 'task',
          title: t.title,
          courseId: t.courseId || '',
          mode: 'work',
          kindKey: ser ? ser.kind : 'task',
          dueMs: earliest(ser ? ser.due : null, dl ? dl.due : null),
          releaseMs: C.NOW,
          estMin: H.estBaseline(t),
          seriesKind: ser ? ser.kind : null,
          attrs: t.attrs || null,
          loggedMin: (num(H.elapsedSec(t), 0)) / 60,
          refs: {
            taskId: t.id || null,
            deadlineId: t.deadlineId || null,
            templateId: ser ? ser.templateId : null,
            occ: ser ? ser.occ : null,
          },
          pinned: typeof t.start === 'number',
          pinnedAt: typeof t.start === 'number' ? t.start : null,
        }));
      }, null);
    });

    /* 2 — the occurrences a series will produce but has not produced yet.
       generateSeries() materialises three weeks; a term is sixteen. Walking
       the template here puts every Tuesday of the term on the gauge without
       filling the task list with a semester of rows nobody has looked at. */
    list((S.series || {}).templates).forEach((tpl) => {
      attempt(() => {
        if (!tpl || tpl.paused || !tpl.id) return;
        const occs = list(H.occurrences(tpl, C.weekStartMs, C.horizonEndMs));
        const releaseFloor = tpl.from ? H.fromIsoDay(tpl.from) : null;
        occs.forEach((o) => {
          attempt(() => {
            if (!o || o.skipped || !o.due) return;
            const key = `series:${tpl.id}:${o.occ}`;
            if (C.byKey.has(key)) return;
            if (H.instanceOf(tpl.id, o.occ)) return;         // source 1 has it
            claim(C, newDemand(C, {
              key,
              origin: 'series',
              title: tpl.title,
              courseId: tpl.courseId || '',
              mode: 'work',
              kindKey: tpl.kind,
              dueMs: o.due,
              releaseMs: Math.max(C.NOW, num(releaseFloor, C.NOW)),
              estMin: num(tpl.estMin, 0) || 60,
              seriesKind: tpl.kind,
              attrs: null,
              refs: { templateId: tpl.id, occ: o.occ, deadlineId: tpl.deadlineId || null },
            }));
          }, null);
        });
      }, null);
    });

    /* 3 — the week's own work items, read raw rather than through wkItems():
       a group filter must change what you see, never what is planned. And
       projected across the whole horizon rather than through itemDays(), which
       answers only about this week — a weekly pset was otherwise absent from
       every horizon week but this one. */
    list(wk.items).forEach((it) => {
      attempt(() => {
        if (!it || !it.id || H.wkMode(it.kind) !== 'work') return;
        const at = H.hhmmToMin(it.time);
        const days = [];
        if (it.recur === 'once') {
          const ms = H.fromIsoDay(it.date);
          const i = ms == null ? -1 : dayIndexOf(C, ms);
          if (i >= 0 && i < C.dayCount) days.push(i);
        } else {
          const want = list(it.days);
          if (want.length) for (let i = 0; i < C.dayCount; i++) if (want.includes(C.cal[i].jsDow)) days.push(i);
        }
        days.forEach((i) => {
          const day = C.cal[i];
          /* For a work item the time is the moment it is due, not the moment it
             happens — the label is swapped in the editor for exactly this
             reason. An item with no time is owed by the end of its day, and the
             last minute of the day is used rather than the first of the next so
             the obligation cannot quietly slide into tomorrow. */
          claim(C, newDemand(C, {
            key: `item:${it.id}:${day.iso}`,
            origin: 'item',
            title: it.title,
            courseId: it.courseId || '',
            mode: 'work',
            kindKey: it.kind,
            dueMs: day.dayMs + (at == null ? 1439 : at) * MS_MIN,
            releaseMs: C.NOW,
            estMin: num(it.estMin, 0),
            seriesKind: WK_TO_SERIES[it.kind] || null,
            attrs: null,
            itemId: it.id,
            refs: { itemId: it.id },
          }));
        });
      }, null);
    });

    /* 4 — habits, for this week only. A habit has no due moment and only this
       week is ever drawn; what the rest of the term gets instead is a reserve
       against its capacity, because the engine has no basis for choosing which
       Thursday of week nine the gym is on. */
    list(wk.items).forEach((it) => {
      attempt(() => {
        if (!it || !it.id || H.wkMode(it.kind) !== 'habit') return;
        const per = H.clamp(Math.round(num(it.perWeek, 0)), 0, 21);
        for (let n = 0; n < per; n++) {
          claim(C, newDemand(C, {
            key: `habit:${it.id}:${n}`,
            origin: 'habit',
            title: it.title,
            courseId: it.courseId || '',
            mode: 'habit',
            kindKey: it.kind,
            dueMs: null,
            releaseMs: C.NOW,
            durMin: num(it.durMin, 0) || 60,
            pricingMode: 'fixed',
            itemId: it.id,
            refs: { itemId: it.id },
          }));
        }
      }, null);
    });

    /* 5 — the horizon's own entries. A trip is never work; an entry with no
       effort is a milestone rather than an obligation; and a span is shared out
       across the weeks it touches rather than counted whole in each of them,
       which would paint four weeks crunch on the strength of one commitment. */
    list((S.longterm || {}).entries).forEach((e) => {
      attempt(() => {
        if (!e || !e.id || e.kind === 'trip') return;
        const startBy = typeof e.startBy === 'number' ? e.startBy : H.fromIsoDay(e.startBy);
        const release = Math.max(C.NOW, num(startBy, num(e.start, C.NOW)));
        const isSpan = typeof e.start === 'number' && e.start < e.end - H.DAY_MS;

        if (!isSpan) {
          claim(C, newDemand(C, {
            key: `entry:${e.id}`,
            origin: 'entry',
            title: e.title,
            courseId: e.courseId || '',
            mode: 'work',
            kindKey: e.kind,
            dueMs: e.end,
            releaseMs: release,
            estMin: num(e.effortMin, 0),
            entryId: e.id,
            refs: { entryId: e.id },
          }));
          return;
        }

        const touched = [];
        for (let w = 0; w < C.weekCount; w++) {
          const from = C.cal[w * 7].dayMs;
          const to = w * 7 + 7 < C.dayCount ? C.cal[w * 7 + 7].dayMs : C.horizonEndMs + 1;
          if (e.start < to && e.end >= from) touched.push({ w, from, to });
        }
        if (!touched.length) touched.push({ w: 0, from: C.cal[0].dayMs, to: C.cal[0].dayMs + 7 * H.DAY_MS });

        const whole = priceOf({
          title: e.title, courseId: e.courseId, estMin: num(e.effortMin, 0),
          seriesKind: null, attrs: null, mode: 'work',
        }, C.lens);
        const share = Math.floor(whole.min / touched.length);
        const shareHigh = Math.floor(whole.high / touched.length);

        const fraction = { 2: 'half', 3: 'third', 4: 'quarter', 5: 'fifth', 6: 'sixth', 7: 'seventh', 8: 'eighth' }[touched.length]
          || `1/${touched.length}`;

        touched.forEach((t, idx) => {
          const last = idx === touched.length - 1;
          const mins = last ? whole.min - share * (touched.length - 1) : share;
          const high = last ? whole.high - shareHigh * (touched.length - 1) : shareHigh;
          claim(C, newDemand(C, {
            key: `entry:${e.id}:${t.w}`,
            origin: 'entry',
            title: e.title,
            courseId: e.courseId || '',
            mode: 'work',
            kindKey: e.kind,
            dueMs: Math.min(e.end, t.to - MS_MIN),
            releaseMs: Math.max(release, t.from),
            estMin: num(e.effortMin, 0),
            entryId: e.id,
            refs: { entryId: e.id, weekIndex: t.w },
            priced: {
              min: mins, high: Math.max(mins, high), rung: whole.rung, n: whole.n,
              /* A span that only ever touches one week is not a share of
                 anything and must not be described as one. */
              basis: touched.length > 1 ? `${whole.basis} — a ${fraction} of it in this week` : whole.basis,
            },
            spanWholeMin: whole.min,
            spanParts: touched.length,
            spanPart: true,
          }));
        });
      }, null);
    });

    /* 6 — deadlines. A deadline is a moment, never work. Where tasks are filed
       against one, the tasks are the work and the deadline only re-dates them —
       which is the same double count the old projLeft arithmetic produced,
       killed structurally rather than by a filter, and a strict pricing upgrade
       besides: five finished psets priced per task reach a measured rung, while
       the deadline's own figure was the global mean multiple over unlike work. */
    list(S.deadlines).forEach((d) => {
      attempt(() => {
        if (!d || !d.id) return;
        const open = list(H.tasksForDeadline(d.id)).filter((t) => t && !t.done);
        if (open.length) {
          open.forEach((t) => {
            const key = t.series && t.series.templateId ? `series:${t.series.templateId}:${t.series.occ}` : `task:${t.id}`;
            const x = C.byKey.get(key);
            if (x) x.dueMs = earliest(x.dueMs, d.due);
          });
          return;
        }
        claim(C, newDemand(C, {
          key: `deadline:${d.id}`,
          origin: 'deadline',
          title: d.title,
          courseId: d.courseId || '',
          mode: 'work',
          kindKey: 'deadline',
          dueMs: d.due,
          releaseMs: C.NOW,
          estMin: 0,
          refs: { deadlineId: d.id },
          priced: { min: 0, high: 0, rung: 'estimate', n: 0, basis: 'nothing is filed against this yet' },
        }));
      }, null);
    });

    pairUp(C);

    /* Things on the board with no time on them are counted and never priced.
       The app refuses an estimate of zero when a wandering thought is promoted
       into a task, and inventing one here would be the same lie with a nicer
       typeface — it is exactly what the bottom rung exists to refuse. */
    C.unpriced = list(S.wanderTasks).filter((w) => w && w.state === 'open' && !w.taskId).length
      + list(S.thoughts).reduce((a, t) => a + list(t && t.tasks).filter((w) => w && !w.done && !w.taskId).length, 0);
  }

  /* ─────────────────── §3.5 the two fuzzy pairings ─────────────────── */

  /* Fuzzy matching is confined to the two places where the app genuinely has no
     shared identity field, and members of one class are never compared to each
     other — which is where "Reading 4 and Reading 5 became one reading" came
     from. taskShape() strips digits by design, so two readings for one course
     on one Friday look identical to it; they are simply never asked. */

  function pairUp(C) {
    const shapeOf = (d) => d.shapeKey;
    const dayOf = (d) => (d.dueMs == null ? '' : H.isoDay(d.dueMs));
    const softA = (d) => `A|${d.courseId || ''}|${shapeOf(d)}|${dayOf(d)}`;
    const softB = (d) => `B|${d.courseId || ''}|${shapeOf(d)}|${dayOf(d)}|${d.dueMs == null ? '' : H.minToHhmm(minOfDay(d.dueMs))}`;

    /** @type {Map<string, any>} */
    const winnersA = new Map();
    /** @type {Map<string, any>} */
    const winnersB = new Map();
    C.demands.forEach((d) => {
      if (d.dueMs == null) return;
      if (d.origin === 'task' || d.origin === 'deadline') {
        const k = softA(d);
        if (!winnersA.has(k)) winnersA.set(k, d);
      }
      if (d.origin === 'task' || d.origin === 'series') {
        const k = softB(d);
        if (!winnersB.has(k)) winnersB.set(k, d);
      }
    });

    const survivors = [];
    C.demands.forEach((d) => {
      /** @type {any} the demand that absorbs this one, if any */
      let host = null;
      let why = '';
      if (d.origin === 'entry' && !d.refs.weekIndex && d.dueMs != null) {
        host = winnersA.get(softA(d)) || null;
        why = 'the same course, the same day, the same shape — the timed copy is the one that carries measurements';
      } else if (d.origin === 'item') {
        host = winnersB.get(softB(d)) || null;
        why = 'the same course, the same day, the same hour — the series copy can be measured per unit';
      }
      if (host && host !== d) {
        host.merged.push({ origin: d.origin, title: d.title, why });
        d.absorbed = true;
        d.absorbedBy = host.key;
        d.verdict = { kind: 'MERGED', latestStartMs: null, slackMin: 0, sharedWith: 0, unplacedMin: 0,
          sentence: `counted once — also filed as “${host.title}”` };
        C.absorbed.push(d);
        return;
      }
      survivors.push(d);
    });

    C.demands = survivors;
    C.byKey = new Map();
    survivors.forEach((d) => C.byKey.set(d.key, d));
  }

  /* ─────────────────── §6.2 the backward pass ─────────────────── */

  /* Latest deadline first, each allocated from the latest capacity that will
     hold it. Nothing here is ever drawn: this pass exists to answer whether the
     work fits at all, and to say which day is the last one on which each thing
     can still begin. */

  function backwardPass(C) {
    const scratch = C.cal.map((day) => ({ i: day.i, dayMs: day.dayMs, gaps: day.gaps, freeMin: day.freeMin, leftMin: day.freeMin }));
    C.scratch = scratch;

    const queue = C.demands
      .filter((d) => d.mode === 'work' && !d.pinned && d.dueMs != null && d.owedMin > 0)
      .sort((a, b) => (b.dueMs - a.dueMs) || cmpStr(a.key, b.key));

    /* Running totals, so that "how much is free between now and Thursday" is a
       lookup rather than a walk. On a sixteen-week term with four hundred
       obligations the walk is forty thousand additions and an array copy for
       each of them, all of it recomputing the same sums. */
    const freeUpTo = new Array(C.dayCount).fill(0);
    for (let i = 0; i < C.dayCount; i++) {
      freeUpTo[i] = (i ? freeUpTo[i - 1] : 0) + (i >= C.todayIdx ? scratch[i].freeMin : 0);
    }

    queue.forEach((d) => {
      let need = ceilTo5(d.owedMin);
      const rawIdx = dayIndexOf(C, d.dueMs);
      const dueD = H.clamp(rawIdx, 0, C.dayCount - 1);
      const floorD = Math.max(0, dayIndexOf(C, d.releaseMs));
      d.overdue = d.dueMs < C.NOW;

      /* A due moment already gone offers nothing. The clamp puts it on day
         zero, and the minute used there is now rather than the hour it was
         owed, so the whole of it lands in unplaced — which is the fact the
         plan then states rather than schedules its way around. */
      const dueMinute = rawIdx < 0 ? 0
        : (d.overdue ? Math.min(minOfDay(d.dueMs), minOfDay(C.NOW)) : minOfDay(d.dueMs));

      for (let x = Math.min(dueD, C.dayCount - 1); x >= floorD && need > 0; x--) {
        const day = scratch[x];
        const room = x === dueD ? Math.min(day.leftMin, capacityBefore(day, dueMinute)) : day.leftMin;
        d.capacityWalk.push({
          dayIdx: x,
          dayLabel: dayLabel(C, x),
          freeMin: day.freeMin,
          note: C.cal[x].isProtected ? `away — ${C.cal[x].protectedBy}` : '',
        });
        const take = Math.min(room, need);
        if (take <= 0) continue;
        day.leftMin -= take;
        d.claims.unshift({ dayIdx: x, min: take });
        need -= take;
      }

      d.unplacedMin = need;
      d.criticalDayIdx = d.claims.length ? d.claims[0].dayIdx : (d.dueMs == null ? C.dayCount : 0);
      d.latestStartMs = d.claims.length ? scratch[d.criticalDayIdx].dayMs : null;
      d.freeBeforeDue = freeUpTo[Math.min(dueD, C.dayCount - 1)];
    });

    /* Float, and who else is counting on it. Four things each honestly
       reporting three hours spare about the same Tuesday is how critical-path
       tools lie, so the number is said once and the company it keeps is said
       with it. */
    const caps = scratch.map((c) => c.freeMin).filter((m) => m > 0);
    const medianDayCap = attempt(() => H.median(caps), 0) || 1;
    const laterThan = new Array(C.dayCount + 2).fill(0);
    queue.forEach((d) => { laterThan[Math.min(d.criticalDayIdx, C.dayCount)] += 1; });
    for (let i = laterThan.length - 2; i >= 0; i--) laterThan[i] += laterThan[i + 1];

    /* Taken after the pass, when every leftMin is final, so this is one sweep
       rather than one per demand. */
    const leftUpTo = new Array(C.dayCount + 1).fill(0);
    for (let i = 0; i < C.dayCount; i++) leftUpTo[i + 1] = leftUpTo[i] + scratch[i].leftMin;

    queue.forEach((d) => {
      d.slackMin = d.unplacedMin > 0
        ? -d.unplacedMin
        : leftUpTo[H.clamp(d.criticalDayIdx, 0, C.dayCount)];
      d.slackDays = d.slackMin / medianDayCap;
      d.sharedWith = Math.max(0, laterThan[Math.min(d.criticalDayIdx + 1, C.dayCount + 1)]);
    });

    C.queued = queue;
  }

  /* ─────────────────── §5 the week, laid out ─────────────────── */

  /* Both session bounds come from the user's own pomodoro settings. A stretch
     shorter than one work interval cannot hold work at all, and a stretch
     longer than her own long-break cycle is a block she has never once
     completed. Neither number is chosen here. */

  function sessionBounds(C) {
    const t = C.S.timer || {};
    const workMin = H.clamp(Math.round(num(t.workMin, 25)) || 25, 5, 240);
    const perLong = H.clamp(Math.round(num(t.perLong, 4)) || 4, 1, 12);
    const breakMin = H.clamp(Math.round(num(t.breakMin, 5)), 0, 60);
    C.MIN_SESSION = workMin;
    C.MAX_SESSION = Math.max(workMin, workMin * perLong + breakMin * (perLong - 1));
  }

  /** How a demand is broken into sittings, and how long each one is. */
  function sessionLengths(C, d) {
    const owed = d.owedMin;
    if (owed <= 0) return [];
    if (!d.splittable) {
      const one = Math.min(owed, C.MAX_SESSION);
      d.shortfallMin = Math.max(0, owed - one);
      return [Math.max(5, roundTo5(one))];
    }
    const n = Math.max(1, Math.ceil(owed / C.MAX_SESSION));
    const even = Math.max(C.MIN_SESSION, roundTo5(owed / n));
    const out = [];
    let left = owed;
    for (let i = 0; i < n; i++) {
      const last = i === n - 1;
      let len = last ? left : even;
      len = Math.min(Math.max(5, roundTo5(len)), C.MAX_SESSION);
      if (len <= 0) break;
      out.push(len);
      left -= len;
    }
    return out.length ? out : [Math.max(5, roundTo5(owed))];
  }

  /* Placement is a lexicographic sort and not a weighted score. Rendering
     0.81 × 35 next to 0.00 × 15 gives a guess and a measurement the same
     typographic authority, and there is not one invented magnitude in here:
     every key below is structural or is a count of something the user did. */
  function cmpCandidate(a, b) {
    return (a.dayIdx - b.dayIdx)
      || (a.missBucket - b.missBucket)
      || (a.leavesStub - b.leavesStub)
      || (a.from - b.from)
      || (a.gapIndex - b.gapIndex);
  }

  function bestSlot(C, work, len, allowSameDay) {
    const from = Math.max(C.todayIdx, work.releaseIdx, 0);
    const to = Math.min(6, work.dueIdx);
    const cands = [];
    const refused = [];

    for (let x = from; x <= to; x++) {
      const day = work.cal[x];
      if (!day || day.isProtected) continue;
      if (!allowSameDay && work.daysUsed.has(x)) continue;
      for (let gi = 0; gi < day.gaps.length; gi++) {
        const g = day.gaps[gi];
        if (g.to - g.from < len) continue;
        if (work.dueMin != null && x === work.dueIdx && g.from + len > work.dueMin) {
          refused.push({ day: C.cal[x].iso, from: H.minToHhmm(g.from), reason: 'past the due moment' });
          continue;
        }
        const rest = (g.to - g.from) - len;
        cands.push({
          dayIdx: x,
          gapIndex: gi,
          from: g.from,
          rest,
          missBucket: (C.lens.missSlots.get(`${day.jsDow}|${Math.floor(g.from / 60)}`) || 0) >= 2 ? 1 : 0,
          leavesStub: rest > 0 && rest < C.MIN_SESSION ? 1 : 0,
        });
      }
    }

    if (!cands.length) return { slot: null, runners: refused.slice(0, 2), cands };
    cands.sort(cmpCandidate);
    const winner = cands[0];

    /* The runners-up, with the key that lost, because a plan that cannot say
       what it turned down is a plan you cannot argue with. */
    const runners = cands.slice(1, 3).map((c) => ({
      day: C.cal[c.dayIdx].iso,
      from: H.minToHhmm(c.from),
      reason: c.dayIdx > winner.dayIdx ? 'later'
        : c.missBucket > winner.missBucket ? 'a slot you have missed twice'
          : c.leavesStub > winner.leavesStub ? `would leave ${H.fmtDurMin(c.rest)} nobody can use`
            : 'later in the day',
    })).concat(refused).slice(0, 2);

    return { slot: winner, runners, cands };
  }

  const dueLabel = (d) => (d.dueMs == null
    ? 'the end of the week'
    : `${DOW[new Date(d.dueMs).getDay()]} ${H.fmtMinOfDay(minOfDay(d.dueMs))}`);

  /* The three-clause grammar the block wears on its face: what it costs and on
     what evidence, what is owed against what is free, and the constraint that
     actually decided the slot. */
  function whyFor(C, d, len, slot, cands) {
    const shape = d.seriesKind
      ? attempt(() => H.seriesKind(d.seriesKind).label.toLowerCase(), 'this')
      : attempt(() => H.taskShape(d.title), 'this');

    let third;
    const laterExists = cands.some((c) => c.dayIdx > slot.dayIdx);
    const missBeaten = cands.find((c) => c.missBucket > slot.missBucket && c.dayIdx === slot.dayIdx);

    if (d.unplacedMin > 0 && d.overdue) {
      third = `it was owed ${dueLabel(d)} and this is the earliest hour left`;
    } else if (d.unplacedMin > 0) {
      third = `only ${H.fmtDurMin(d.freeBeforeDue)} of real gaps left before ${dueLabel(d)} — ${H.fmtDurMin(d.unplacedMin)} short`;
    } else if (!laterExists && d.dueMs != null) {
      third = `this is the last ${H.fmtDurMin(len)} that still fits before ${dueLabel(d)}`;
    } else if (missBeaten) {
      third = `${DOW[C.cal[missBeaten.dayIdx].jsDow]} at ${H.minToHhmm(missBeaten.from)} is a slot you have missed twice`;
    } else if (d.rung === 'multiple' || d.rung === 'estimate') {
      third = `nothing measured for ${shape} yet, so this is planned early enough to be wrong`;
    } else {
      third = `${H.fmtDurMin(Math.max(0, d.slackMin))} spare`;
      if (d.sharedWith > 0) {
        third += ` — the same ${H.fmtDurMin(Math.max(0, d.slackMin))} ${d.sharedWith} other thing${d.sharedWith === 1 ? '' : 's'} ${d.sharedWith === 1 ? 'is' : 'are'} counting on`;
      }
    }

    /* "55m of 1h 50m" when this is one sitting of several; just "1h 50m" when
       the sitting is the whole of it, because the other half of that sentence
       is the same number twice. */
    const cost = len >= d.owedMin ? H.fmtDurMin(len) : `${H.fmtDurMin(len)} of ${H.fmtDurMin(d.owedMin)}`;
    return [cost, d.basis, third].filter(Boolean).join(' — ');
  }

  function blockId(key, ordinal) { return idSafe(`blk:${key}:${ordinal}`); }

  function makeWeek(C) {
    const S = C.S;
    const wk = S.weekly || {};
    const blocks = [];

    /* The solver's own copy of the first seven days. The ledger the trace is
       drawn from stays as it was measured; what gets consumed here is a
       duplicate, so the drawer can still show what the week had before
       anything was put in it. */
    const cal = C.cal.slice(0, 7).map((day) => ({
      i: day.i, iso: day.iso, jsDow: day.jsDow, isProtected: day.isProtected,
      gaps: day.gaps.map((g) => ({ from: g.from, to: g.to })),
    }));

    const consume = (day, gapIndex, from, len) => {
      const g = day.gaps[gapIndex];
      const rest = { from: from + len, to: g.to };
      if (rest.to > rest.from) day.gaps[gapIndex] = rest;
      else day.gaps.splice(gapIndex, 1);
    };

    /* Which demands can be worked on this week at all. A demand released after
       Sunday is not this week's business; one whose due moment has already gone
       is treated as due today and uncapped, so it is placed rather than dropped. */
    const works = [];
    C.demands.forEach((d) => {
      if (d.mode !== 'work' || d.pinned || d.min <= 0 || d.owedMin <= 0) return;
      const releaseIdx = Math.max(0, dayIndexOf(C, d.releaseMs));
      if (releaseIdx > 6) return;
      const rawDue = dayIndexOf(C, d.dueMs == null ? C.horizonEndMs : d.dueMs);
      const overdue = d.dueMs != null && d.dueMs < C.NOW;
      const dueIdx = overdue ? 6 : Math.min(6, rawDue);
      if (dueIdx < C.todayIdx) return;
      const lens = sessionLengths(C, d);
      d.sessionLens = lens;
      d.sessionsLeft = lens.length;
      d.overdue = overdue;

      /* What this week actually owes, which is not the same as what it is
         allowed to get on with. Anything due inside these seven days is owed in
         full; anything due later is owed only the share the backward pass
         claimed here, and whatever else gets placed early is buffer rather than
         obligation. Counting the whole term as this week's debt would make the
         balance sentence read four hundred hours short, which is true of the
         term and a lie about the week. */
      d.weekOwedMin = (overdue || rawDue <= 6)
        ? d.owedMin
        : d.claims.reduce((a, c) => a + (c.dayIdx <= 6 ? c.min : 0), 0);

      works.push({
        d, cal, releaseIdx, dueIdx,
        dueMin: overdue || rawDue > 6 || d.dueMs == null ? null : minOfDay(d.dueMs),
        daysUsed: new Set(), usedOrdinals: new Set(),
      });
    });

    const byWork = new Map();
    works.forEach((w) => byWork.set(w.d.key, w));

    /* Phase 0 — the user's own rulings, which are senior to everything below.
       A block she moved is re-emitted exactly where she put it and its hours
       were already booked as busy before any of this ran. A block she missed is
       frozen at the slot it died in, keeps its id so the alternatives path
       still answers, and — this is the point of it — does not reduce what is
       still owed. A miss records that a slot was a fiction, not that the work
       went away. */
    const emitRuling = (w, id, dayIso, startMin, endMin, why, ruling) => {
      const dayIdx = cal.findIndex((c) => c.iso === dayIso);
      if (dayIdx < 0 || startMin == null || endMin == null || endMin <= startMin) return;
      const d = w.d;
      blocks.push({
        id,
        day: dayIso,
        start: H.minToHhmm(startMin),
        end: H.minToHhmm(endMin),
        itemId: d.itemId || null,
        title: d.title,
        courseId: d.courseId || '',
        kind: d.mode,
        rung: d.rung,
        why,
        traceKey: d.key,
      });
      d.placed.push({ blockId: id, day: dayIso, from: H.minToHhmm(startMin), to: H.minToHhmm(endMin), why, rejected: [] });
      w.daysUsed.add(dayIdx);
      const ord = ordinalOf(id);
      if (ord != null) w.usedOrdinals.add(ord);
      if (ruling === 'moved') {
        d.ruled = true;
        if (w.d.sessionsLeft > 0) w.d.sessionsLeft -= 1;
        d.committedMin = (d.committedMin || 0) + (endMin - startMin);
      }
      /* A missed slot is not free for something else to be put into either —
         the ledger keeps it, the solver does not. */
      const day = cal[dayIdx];
      for (let gi = 0; gi < day.gaps.length; gi++) {
        const g = day.gaps[gi];
        if (startMin >= g.from && endMin <= g.to) {
          const before = { from: g.from, to: startMin };
          const after = { from: endMin, to: g.to };
          const repl = [before, after].filter((x) => x.to > x.from);
          day.gaps.splice(gi, 1, ...repl);
          break;
        }
      }
    };

    const moved = wk.moved && typeof wk.moved === 'object' ? wk.moved : {};
    Object.keys(moved).sort().forEach((id) => {
      attempt(() => {
        const mv = moved[id];
        if (!mv) return;
        const w = byWork.get(keyOfBlockId(C, id));
        if (!w) return;                              // dangling: the sweep will take it
        emitRuling(w, id, mv.day, H.hhmmToMin(mv.start), H.hhmmToMin(mv.end), 'you moved this here', 'moved');
      }, null);
    });

    list(wk.misses).forEach((m) => {
      attempt(() => {
        if (!m || !m.blockId || !m.day) return;
        if (moved[m.blockId]) return;                // a move supersedes a miss
        const w = byWork.get(keyOfBlockId(C, m.blockId));
        if (!w) return;
        const startMin = H.hhmmToMin(m.start || m.time);
        if (startMin == null) return;
        const endMin = H.hhmmToMin(m.end) != null
          ? H.hhmmToMin(m.end)
          : startMin + (w.d.sessionLens[0] || C.MIN_SESSION);
        emitRuling(w, m.blockId, m.day, startMin, endMin, 'you marked this missed — the work is still owed', 'missed');
      }, null);
    });

    /* Phase 1 — the queue order. Seven keys, every one of them structural or
       transcribed from the app's own words about what gets dropped first.
       Weaker evidence goes earlier: the rung moves *when*, never *how long*. */
    const rungOrder = { rate: 0, median: 1, multiple: 2, estimate: 3, fixed: 4 };
    const dropOrder = { reading: 0, post: 1, pset: 2 };
    const queue = works.slice().sort((a, b) => {
      const x = a.d;
      const y = b.d;
      return ((y.unplacedMin > 0 ? 1 : 0) - (x.unplacedMin > 0 ? 1 : 0))
        || (x.criticalDayIdx - y.criticalDayIdx)
        || ((x.dueMs == null ? Infinity : x.dueMs) - (y.dueMs == null ? Infinity : y.dueMs))
        || ((rungOrder[x.rung] == null ? 9 : rungOrder[x.rung]) - (rungOrder[y.rung] == null ? 9 : rungOrder[y.rung]))
        || ((dropOrder[x.kindKey] == null ? 3 : dropOrder[x.kindKey]) - (dropOrder[y.kindKey] == null ? 3 : dropOrder[y.kindKey]))
        || (y.owedMin - x.owedMin)
        || cmpStr(x.key, y.key);
    });

    const place = (w, slot, len, runners, cands) => {
      const d = w.d;
      const day = w.cal[slot.dayIdx];
      let ord = 0;
      while (w.usedOrdinals.has(ord)) ord++;
      w.usedOrdinals.add(ord);
      const id = blockId(d.key, ord);
      const why = whyFor(C, d, len, slot, cands);
      blocks.push({
        id,
        day: C.cal[slot.dayIdx].iso,
        start: H.minToHhmm(slot.from),
        end: H.minToHhmm(slot.from + len),
        itemId: d.itemId || null,
        title: d.title,
        courseId: d.courseId || '',
        kind: d.mode,
        rung: d.rung,
        why,
        traceKey: d.key,
      });
      d.placed.push({
        blockId: id, day: C.cal[slot.dayIdx].iso,
        from: H.minToHhmm(slot.from), to: H.minToHhmm(slot.from + len),
        why, rejected: runners,
      });
      d.committedMin = (d.committedMin || 0) + len;
      consume(day, slot.gapIndex, slot.from, len);
      w.daysUsed.add(slot.dayIdx);
      d.sessionsLeft -= 1;
    };

    /* Phase 2 and 3 — round-robin, so a week spreads rather than packing
       Monday into a wall. The loop is bounded twice over: every iteration
       either places a session, which strictly reduces what is outstanding, or
       marks a demand stalled, and a round in which everything stalls breaks. */
    const MAX_ROUNDS_FACTOR = 4;
    let rounds = 0;
    let hitCap = false;
    for (;;) {
      let anyLeft = false;
      let anyPlaced = false;
      for (let i = 0; i < queue.length; i++) {
        const w = queue[i];
        if (w.d.sessionsLeft <= 0) continue;
        anyLeft = true;
        const len = w.d.sessionLens[w.d.sessionLens.length - w.d.sessionsLeft];
        const found = bestSlot(C, w, len, false);
        if (!found.slot) continue;
        place(w, found.slot, len, found.runners, found.cands);
        anyPlaced = true;
      }
      if (!anyLeft || !anyPlaced) break;
      rounds++;
      if (rounds >= Math.max(4, queue.length * MAX_ROUNDS_FACTOR)) { hitCap = true; break; }
    }

    /* The final feasibility retry: a genuinely tight week is allowed to stack
       two sittings of one thing on one day, because at that point spreading is
       a preference and finishing is not. */
    queue.forEach((w) => {
      let guard = 0;
      while (w.d.sessionsLeft > 0 && guard++ < 32) {
        const len = w.d.sessionLens[w.d.sessionLens.length - w.d.sessionsLeft];
        const found = bestSlot(C, w, len, true);
        if (!found.slot) break;
        place(w, found.slot, len, found.runners, found.cands);
      }
    });

    /* Phase 4 — habits, last, so they can never crowd out work that is due.
       Spread rather than stacked, which is exactly what the tab already
       promises: the day chosen is the one furthest from the days this habit has
       already taken. */
    const habits = C.demands.filter((d) => d.mode === 'habit' && d.min > 0);
    const habitDays = new Map();
    let habitsPlaced = 0;
    const habitPlacedDays = [];
    habits.forEach((d) => {
      const len = Math.max(5, roundTo5(Math.min(d.owedMin, C.MAX_SESSION)));
      const taken = habitDays.get(d.itemId || d.key) || [];
      /** @type {any} */ let best = null;
      for (let x = Math.max(0, C.todayIdx); x <= 6; x++) {
        const day = cal[x];
        if (!day || day.isProtected) continue;
        const gi = day.gaps.findIndex((g) => g.to - g.from >= len);
        if (gi < 0) continue;
        const distance = taken.length ? Math.min.apply(null, taken.map((t) => Math.abs(t - x))) : Infinity;
        const free = day.gaps.reduce((a, g) => a + (g.to - g.from), 0);
        const cand = { x, gi, distance, free };
        if (!best
          || cand.distance > best.distance
          || (cand.distance === best.distance && cand.free > best.free)
          || (cand.distance === best.distance && cand.free === best.free && cand.x < best.x)) best = cand;
      }
      if (!best) { d.spillMin = d.owedMin; return; }
      const day = cal[best.x];
      const g = day.gaps[best.gi];
      const id = blockId(d.key, 0);
      blocks.push({
        id,
        day: C.cal[best.x].iso,
        start: H.minToHhmm(g.from),
        end: H.minToHhmm(g.from + len),
        itemId: d.itemId || null,
        title: d.title,
        courseId: d.courseId || '',
        kind: 'habit',
        rung: d.rung,
        why: `${H.fmtDurMin(len)} — ${d.basis} — placed in what the rest of the week left`,
        traceKey: d.key,
      });
      d.placed.push({ blockId: id, day: C.cal[best.x].iso, from: H.minToHhmm(g.from), to: H.minToHhmm(g.from + len), why: 'in what the rest of the week left', rejected: [] });
      d.committedMin = len;
      d.sessionsLeft = 0;
      consume(day, best.gi, g.from, len);
      taken.push(best.x);
      habitDays.set(d.itemId || d.key, taken);
      habitsPlaced++;
      habitPlacedDays.push(best.x);
    });

    /* What did not fit, said out loud. Nothing is dropped silently; a demand
       with sessions left emits no block and falls through to the grid's own
       unplanned-items path, which already knows how to draw it. */
    let spill = 0;
    let overflowed = [];
    works.forEach((w) => {
      const d = w.d;
      const mins = Math.max(0, d.weekOwedMin - num(d.committedMin, 0));
      d.spillMin = mins;
      if (mins > 0) { spill += mins; overflowed.push(d); }
    });
    overflowed = overflowed.sort((a, b) => (b.spillMin - a.spillMin) || cmpStr(a.key, b.key));

    const owedTotal = works.reduce((a, w) => a + w.d.weekOwedMin, 0);
    const freeTotal = C.cal.slice(0, 7).reduce((a, day) => a + day.freeMin, 0);
    const notes = weekNotes(C, {
      blocks, works, overflowed, spill, owedTotal, freeTotal,
      habits, habitsPlaced, habitPlacedDays, hitCap, queueLength: queue.length,
    });

    blocks.sort((a, b) => cmpStr(a.day, b.day) || (H.hhmmToMin(a.start) - H.hhmmToMin(b.start)) || cmpStr(a.id, b.id));

    const message = !works.length && !habits.length
      ? 'nothing to lay out yet'
      : spill > 0
        ? `${overflowed.length} thing${overflowed.length === 1 ? '' : 's'} do not fit — ${H.fmtDurMin(spill)} with nowhere to go`
        : `everything fits, ${H.fmtDurMin(Math.max(0, freeTotal - owedTotal))} spare`;

    /* The same week, folded by day. Numbers only — no per-day sentence beyond
       the arithmetic, because seven narrow columns of prose is clutter by
       construction and the conditions that would fire it are loudest on the
       weeks they mean least about.

       `spareMin` goes negative, and that is the interesting case rather than a
       fault: a block the user moved is re-emitted exactly where she put it,
       gaps or no gaps, so a day can hold more than it had room for. Folding
       here rather than at the drawing end is what keeps the dock's numbers and
       the clock's numbers the same numbers. */
    const days = C.cal.slice(0, 7).map((day, i) => {
      const mine = blocks.filter((b) => b.day === day.iso);
      const placedMin = mine.reduce((a, b) => a + (H.hhmmToMin(b.end) - H.hhmmToMin(b.start)), 0);
      const freeMin = num(day.freeMin, 0);
      const spareMin = freeMin - placedMin;
      const headline = !placedMin && !freeMin
        ? 'nothing placed, and no gaps to place into'
        : !placedMin
          ? `nothing placed against ${H.fmtDurMin(freeMin)} of gaps`
          : spareMin < 0
            ? `${H.fmtDurMin(placedMin)} placed against ${H.fmtDurMin(freeMin)} of gaps — `
              + `${H.fmtDurMin(-spareMin)} of it has nowhere to go`
            : `${H.fmtDurMin(placedMin)} placed against ${H.fmtDurMin(freeMin)} of gaps — `
              + `${spareMin ? `${H.fmtDurMin(spareMin)} spare` : 'nothing spare'}`;
      return { iso: day.iso, dayIdx: i, placedMin, freeMin, spareMin, blockIds: mine.map((b) => b.id), headline };
    });

    return {
      status: 'ok',
      plannedAt: C.NOW,
      message,
      weekStart: H.isoDay(C.weekStartMs),
      blocks,
      notes,
      days,
    };
  }

  /** The demand key an emitted block id encodes: the ordinal off the end, the
      `blk:` off the front, and nothing about which day it landed on — which is
      what lets a move survive a re-solve that shifts it to a Wednesday. */
  function keyOfBlockId(C, id) {
    const bare = String(id).replace(/^blk:/, '').replace(/:\d+$/, '');
    if (C.byKey.has(bare)) return bare;
    let found = null;
    C.byKey.forEach((d, key) => { if (!found && idSafe(key) === bare) found = key; });
    return found || bare;
  }

  function ordinalOf(id) {
    const m = /:(\d+)$/.exec(String(id));
    return m ? +m[1] : null;
  }

  /* ─────────────────── §5.9 what the week says about itself ─────────────────── */

  function weekNotes(C, x) {
    const notes = [];
    const envelope = `This assumes you can use the whole ${H.fmtMinOfDay(C.dayStart)}–${H.fmtMinOfDay(C.dayEnd)} window you set — narrow it at the top of the week tab if that is not true.`;

    if (x.spill > 0) {
      const top = x.overflowed[0];
      /* The long-run figure appears only where there is one: at the bottom rung
         the two columns are equal, and printing the same number twice would
         dress a guess as a range. */
      const range = top.high > top.min ? `, which could run to ${H.fmtDurMin(top.high)} if it goes long` : '';
      const others = x.overflowed.slice(1, 3).map((d) => `${d.title} (${H.fmtDurMin(d.spillMin)})`);
      const rest = Math.max(0, x.overflowed.length - 3);

      /* The remedy is the smallest set of placed sessions whose minutes clear
         the deficit, named so it can be argued with rather than obeyed. */
      const droppable = x.blocks
        .filter((b) => b.kind === 'work')
        .map((b) => ({ title: b.title, min: H.hhmmToMin(b.end) - H.hhmmToMin(b.start) }))
        .sort((a, b) => (b.min - a.min) || cmpStr(a.title, b.title));
      const remedy = [];
      let cleared = 0;
      for (let i = 0; i < droppable.length && cleared < x.spill; i++) {
        remedy.push(droppable[i].title);
        cleared += droppable[i].min;
      }

      /* Two quite different kinds of short, and they must not read alike: not
         enough hours in the week at all, and enough hours sitting after the
         moment they were needed. */
      const opening = x.owedTotal > x.freeTotal
        ? `${H.fmtDurMin(x.owedTotal)} owed against ${H.fmtDurMin(x.freeTotal)} free. `
        : `${H.fmtDurMin(x.owedTotal)} owed and ${H.fmtDurMin(x.freeTotal)} free, but the free hours fall after the moments they were wanted. `;

      notes.push({
        kind: 'warning',
        title: `The week is ${H.fmtDurMin(x.spill)} short`,
        body: opening
          + `Most of it is ${top.title} (${top.spillMin >= top.weekOwedMin ? H.fmtDurMin(top.spillMin) : `${H.fmtDurMin(top.spillMin)} of ${H.fmtDurMin(top.weekOwedMin)}`} — ${top.basis})${range}. `
          + (others.length ? `Then ${others.join(' and ')}. ` : '')
          + (rest > 0 ? `${rest} other thing${rest === 1 ? '' : 's'} did not fit either. ` : '')
          + (cleared < x.spill
            ? `Nothing already placed can be dropped to clear it — the hours are not there. ${envelope}`
            : remedy.length <= 3
              /* Named only while the list is short enough to act on. "Drop
                 these three" is a remedy; "drop eleven of these" is arithmetic
                 wearing a remedy's clothes. */
              ? `Dropping ${remedy.join(', ')}, or working past ${H.fmtMinOfDay(C.dayEnd)}, clears it.`
              : `It would take ${remedy.length} of the sessions already placed coming off to clear it, or a day longer than ${H.fmtMinOfDay(C.dayStart)}–${H.fmtMinOfDay(C.dayEnd)}.`),
      });
    } else if (x.owedTotal > 0) {
      const weakMin = x.works.filter((w) => w.d.rung === 'multiple' || w.d.rung === 'estimate')
        .reduce((a, w) => a + w.d.weekOwedMin, 0);
      /* Said only when most of the week rests on guesses, because a margin
         made of estimates is not the same margin. */
      const thin = weakMin / x.owedTotal > 0.5
        ? ` ${H.fmtDurMin(weakMin)} of that is your own estimate rather than anything measured, so treat the margin as thin.`
        : '';
      notes.push({
        kind: 'free',
        title: `The week balances with ${H.fmtDurMin(Math.max(0, x.freeTotal - x.owedTotal))} spare`,
        body: `${H.fmtDurMin(x.owedTotal)} owed against ${H.fmtDurMin(x.freeTotal)} of gaps.${thin} ${envelope}`,
      });
    } else {
      /* Nothing is due before Sunday. Any blocks on the grid are a head start
         on what comes after it, and saying so is the difference between an
         empty week and a week whose work has already been brought forward. */
      const ahead = x.blocks.filter((b) => b.kind === 'work').length;
      notes.push({
        kind: 'free',
        title: 'Nothing is owed this week',
        body: `${H.fmtDurMin(x.freeTotal)} of gaps and nothing due before Sunday.`
          + (ahead ? ` The ${ahead} session${ahead === 1 ? '' : 's'} laid out ${ahead === 1 ? 'is' : 'are'} a head start on what comes after.` : '')
          + ` ${envelope}`,
      });
    }

    if (x.habits.length) {
      /* Named after the habit when there is only one of them, because "3 gym
         slots on Tue, Thu and Sun" is the sentence she would say herself. */
      const titles = x.habits.map((d) => d.title).filter((t, i, all) => t && all.indexOf(t) === i);
      const label = titles.length === 1 ? titles[0] : 'habit';
      const days = x.habitPlacedDays.slice().sort((a, b) => a - b).map((i) => DOW[C.cal[i].jsDow]);
      const uniqueDays = days.filter((d, i) => days.indexOf(d) === i);
      const listed = uniqueDays.length > 1
        ? `${uniqueDays.slice(0, -1).join(', ')} and ${uniqueDays[uniqueDays.length - 1]}`
        : uniqueDays.join('');

      if (x.habitsPlaced === x.habits.length) {
        notes.push({
          kind: 'gym',
          title: `${x.habitsPlaced} ${label} slot${x.habitsPlaced === 1 ? '' : 's'}${listed ? ` on ${listed}` : ''}`,
          body: 'These go in whatever the rest of the week leaves, spread out rather than stacked.',
        });
      } else if (x.habitsPlaced === 0) {
        notes.push({
          kind: 'gym',
          title: `None of your ${x.habits.length} ${label} slot${x.habits.length === 1 ? '' : 's'} fit this week`,
          body: 'Work is placed first on purpose and it took everything. This is what the week costs, said out loud rather than quietly dropped.',
        });
      } else {
        notes.push({
          kind: 'gym',
          title: `Only ${x.habitsPlaced} of your ${x.habits.length} ${label} slots fit${listed ? ` — ${listed}` : ''}`,
          body: 'The rest of the week left no stretch long enough. Work is placed first on purpose, so this is what a heavy week costs.',
        });
      }
    }

    if (C.unpriced > 0) {
      notes.push({
        kind: 'note',
        title: `${C.unpriced} thing${C.unpriced === 1 ? '' : 's'} on the board with no estimate`,
        body: 'None of them are in these numbers. Give one an estimate and it becomes a task the plan can place.',
      });
    }

    if (x.hitCap) {
      notes.push({
        kind: 'warning',
        title: 'The layout stopped early',
        body: `${x.queueLength} things were still being placed when the solver reached its own limit. Nothing was dropped silently — what is missing is what is missing.`,
      });
    }

    const syncedAt = num(C.SY.syncedAt, 0);
    const staleDays = syncedAt ? Math.floor((C.NOW - syncedAt) / H.DAY_MS) : 0;
    if (syncedAt && staleDays > 7) {
      notes.push({
        kind: 'warning',
        title: `Your Google calendar was last synced ${staleDays} days ago`,
        body: 'Anything added there since is not subtracted from this week.',
      });
    }

    return notes;
  }

  /* ─────────────────── §6 the term, folded into weeks ─────────────────── */

  function makePlan(C) {
    const weeks = [];
    const committed = new Array(C.weekCount).fill(0);
    const capacity = new Array(C.weekCount).fill(0);
    const weak = new Array(C.weekCount).fill(0);
    const exposure = new Array(C.weekCount).fill(0);
    const biggest = new Array(C.weekCount).fill(null);
    const spanNote = new Array(C.weekCount).fill(null);

    /* Habits past this week would otherwise vanish and overstate what is free.
       They are held back as a week-level reserve rather than a day-level
       booking, because there is no honest basis for choosing which Thursday of
       week nine the gym is on. */
    const reserve = list((C.S.weekly || {}).items).reduce((a, it) => {
      if (!it || attempt(() => H.wkMode(it.kind), '') !== 'habit') return a;
      return a + H.clamp(Math.round(num(it.perWeek, 0)), 0, 21) * (num(it.durMin, 0) || 60);
    }, 0);

    C.cal.forEach((day) => { capacity[Math.floor(day.i / 7)] += day.freeMin; });
    for (let w = 1; w < C.weekCount; w++) capacity[w] = Math.max(0, capacity[w] - reserve);

    /* What a week is committed to is the backward pass's allocation — the days
       it claimed for each obligation — plus, for the week a thing is due in,
       whatever could not be allocated anywhere at all. That second term is the
       week's real problem and it belongs in the week's own number rather than
       only in an insight.

       It is deliberately not "what the forward pass placed here". The forward
       pass will happily pull November's reading into a quiet Thursday, which is
       the right thing to do with a quiet Thursday and the wrong thing to say
       about the week: the headline claims this much work *must* happen here,
       and a band is a verdict. Work it merely got a head start on shows up
       where it is honest — as a wide gauge bar over a calmer band. */
    C.demands.forEach((d) => {
      if (d.mode !== 'work') return;
      const weakRung = d.rung === 'multiple' || d.rung === 'estimate';
      const ratio = d.min > 0 ? d.high / d.min : 1;

      const add = (w, min) => {
        if (w < 0 || w >= C.weekCount || min <= 0) return;
        committed[w] += min;
        exposure[w] += min * ratio;
        if (weakRung) weak[w] += min;
        if (!biggest[w] || min > biggest[w].min) biggest[w] = { d, min };
        if (d.spanParts > 1 && (!spanNote[w] || min > spanNote[w].min)) spanNote[w] = { d, min };
      };

      d.claims.forEach((c) => add(Math.floor(c.dayIdx / 7), c.min));
      if (d.unplacedMin > 0 && d.dueMs != null) {
        add(Math.floor(H.clamp(dayIndexOf(C, d.dueMs), 0, C.dayCount - 1) / 7), d.unplacedMin);
      }
    });

    for (let w = 0; w < C.weekCount; w++) {
      const com = Math.round(committed[w]);
      const cap = Math.round(capacity[w]);
      const row = { start: C.cal[w * 7].iso };

      /* Omitted entirely when nothing is committed: ltBand(0) answers `empty`,
         the stylesheet has no rule for it, and the horizon prints the value as
         visible text — so a clear week must fall through to the local band and
         read "clear". */
      if (com > 0) row.load = attempt(() => H.ltBand(com / 60).key, 'quiet');

      row.headline = com === 0
        ? 'nothing has to happen here'
        : com <= cap
          ? `${H.fmtDurMin(com)} of work must happen here, and ${H.fmtDurMin(cap)} are free`
          : `${H.fmtDurMin(com)} of work must happen here, and only ${H.fmtDurMin(cap)} are free — ${H.fmtDurMin(com - cap)} over`;

      row.note = weekNote(C, w, { com, cap, weak: Math.round(weak[w]), biggest: biggest[w], span: spanNote[w] });
      weeks.push(row);
    }

    C.weekCommitted = committed;
    C.weekCapacity = capacity;

    const insights = makeInsights(C, { committed, capacity, weak, exposure, biggest });
    const extracted = collectExtracted(C);

    const over = committed.filter((c, i) => c > capacity[i]).length;
    const late = C.demands.filter((d) => d.unplacedMin > 0).length;
    const bits = [];
    if (over) bits.push(`${over} week${over === 1 ? '' : 's'} over`);
    if (late) bits.push(`${late} thing${late === 1 ? '' : 's'} already late`);
    const message = `${bits.length ? `${bits.join(', ')}. ` : 'Nothing is over and nothing is late. '}`
      + `This assumes you can use the whole ${H.fmtMinOfDay(C.dayStart)}–${H.fmtMinOfDay(C.dayEnd)} window you set.`;

    return {
      status: 'ok',
      computedAt: C.NOW,
      message,
      horizon: { weeks: C.weekCount, from: H.isoDay(C.weekStartMs) },
      weeks,
      insights,
      extracted,
    };
  }

  /** Cause and remedy for one week, first match wins. */
  function weekNote(C, w, x) {
    const firstDay = w * 7;
    const away = C.cal.slice(firstDay, firstDay + 7).filter((d) => d.isProtected);

    if (x.com > x.cap && x.biggest) {
      const d = x.biggest.d;
      const canMoveEarlier = dayIndexOf(C, d.releaseMs) < firstDay;
      if (canMoveEarlier) {
        const earlier = C.cal[Math.max(0, firstDay - 7)];
        return `Most of it is ${d.title} (${H.fmtDurMin(x.biggest.min)}). Starting it the week of ${dayLabel(C, earlier.i)} clears this week.`;
      }
      return w === 0
        ? `Nothing here can move earlier — this is the week you are in.`
        : `Nothing here can move earlier — ${d.title} is fixed to this week.`;
    }
    if (x.span) {
      const whole = x.span.d.spanWholeMin || x.span.d.min;
      return `${x.span.d.title} counts ${H.fmtDurMin(x.span.min)} here — a share of its ${H.fmtDurMin(whole)} spread over the weeks it runs, not all of it.`;
    }
    if (away.length) {
      /* A stretch reads as a stretch; scattered days read as a list, because
         "away Sun–Sun" is what a range says about two Sundays a fortnight
         apart and it is not true of either of them. */
      const contiguous = away.length > 1 && away[away.length - 1].i - away[0].i === away.length - 1;
      const names = away.map((d) => DOW[d.jsDow]);
      return contiguous
        ? `You are away ${names[0]}–${names[names.length - 1]}, so those days hold nothing.`
        : `You are away ${names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0]}, so ${names.length > 1 ? 'those days hold' : 'that day holds'} nothing.`;
    }
    if (x.com > 0 && x.weak > 0 && x.weak / x.com > 0.5) {
      return `${H.fmtDurMin(x.weak)} of the ${H.fmtDurMin(x.com)} is your own estimate rather than anything measured.`;
    }
    return '';
  }

  /* ─────────────────── §6.4 the insights ─────────────────── */

  const SEVERITY = { crunch: 0, conflict: 1, 'start-early': 2, opportunity: 3 };

  function makeInsights(C, x) {
    const out = [];
    const weekIsoOf = (ms) => H.isoDay(H.startOfWeek(new Date(Math.max(num(ms, C.weekStartMs), C.weekStartMs))).getTime());

    /* Crunch — the honest verdict of the backward pass, which is the one thing
       here that is a fact rather than a reading. */
    const late = C.demands.filter((d) => d.unplacedMin > 0)
      .sort((a, b) => (b.unplacedMin - a.unplacedMin) || cmpStr(a.key, b.key));
    const crunchWeeks = new Set();

    if (late.length > 3) {
      const first = late.slice().sort((a, b) => (num(a.dueMs, Infinity) - num(b.dueMs, Infinity)) || cmpStr(a.key, b.key))[0];
      out.push({
        kind: 'crunch',
        key: 'crunch:many',
        title: `${late.length} things cannot fit before they are due`,
        body: `The earliest is ${first.title}, due ${dayLabelOf(C, first.dueMs)} — ${H.fmtDurMin(first.unplacedMin)} of it has nowhere to go. `
          + `${H.fmtDurMin(late.reduce((a, d) => a + d.unplacedMin, 0))} is unplaceable in all.`,
        week: weekIsoOf(first.dueMs),
      });
      late.forEach((d) => crunchWeeks.add(weekIsoOf(d.dueMs)));
    } else {
      late.forEach((d) => {
        crunchWeeks.add(weekIsoOf(d.dueMs));
        out.push({
          kind: 'crunch',
          key: `crunch:${d.key}`,
          title: `${d.overdue ? 'Overdue' : 'Already late'}: ${d.title}`,
          body: `${H.fmtDurMin(d.owedMin)} owed, ${H.fmtDurMin(d.freeBeforeDue)} of real gaps before ${dueLabel(d)} — ${H.fmtDurMin(d.unplacedMin)} short.`,
          week: weekIsoOf(d.dueMs),
        });
      });
    }

    for (let w = 0; w < C.weekCount; w++) {
      const com = x.committed[w];
      const cap = x.capacity[w];
      if (com <= cap) continue;
      const iso = H.isoDay(C.cal[w * 7].dayMs);
      if (crunchWeeks.has(iso)) continue;
      const big = x.biggest[w];
      out.push({
        kind: 'crunch',
        key: `crunch:week:${iso}`,
        title: `The week of ${dayLabel(C, w * 7)} is ${H.fmtDurMin(com - cap)} over`,
        body: `${H.fmtDurMin(com)} must happen and ${H.fmtDurMin(cap)} are free`
          + (x.exposure[w] > com ? `, or ${H.fmtDurMin(Math.round(x.exposure[w]))} if all of it runs as long as it has before.` : '.')
          + (big ? ` ${big.d.title} is ${H.fmtDurMin(big.min)} of it.` : ''),
        week: iso,
      });
    }

    /* Start early — a week is the app's own planning unit, so a week of float
       is the line between "plan it" and "get on with it". */
    C.demands.forEach((d) => {
      if (d.unplacedMin > 0 || d.latestStartMs == null) return;
      if (!(d.slackDays >= 0 && d.slackDays < 7)) return;
      if (d.latestStartMs <= C.NOW) return;
      const ins = {
        kind: 'start-early',
        key: `start:${d.key}`,
        title: `Start “${d.title}” by ${DOW[new Date(d.latestStartMs).getDay()]} ${dayLabelOf(C, d.latestStartMs)}`,
        body: `${H.fmtDurMin(d.owedMin)} of work due ${dayLabelOf(C, d.dueMs)}. `
          + `After ${dayLabelOf(C, d.latestStartMs)} the gaps between now and then do not add up to the ${H.fmtDurMin(d.owedMin)} this needs.`,
        week: H.isoDay(H.startOfWeek(new Date(d.latestStartMs)).getTime()),
      };
      /* The button is only offered where it can do something: renderInsights
         looks the entry up by id and silently drops the action otherwise, so an
         insight about a task-view deadline says the date in words instead. */
      if (d.entryId) {
        ins.action = {
          type: 'start-by',
          entryId: d.entryId,
          startBy: H.isoDay(d.latestStartMs),
          label: `Start by ${dayLabelOf(C, d.latestStartMs)}`,
        };
      }
      out.push(ins);
    });

    /* Conflict — where two things the user has already said cannot both be
       true. */
    const away = [];
    C.demands.forEach((d) => {
      if (d.dueMs == null || d.mode !== 'work' || d.min <= 0) return;
      const i = dayIndexOf(C, d.dueMs);
      if (i < 0 || i >= C.dayCount) return;
      if (!C.cal[i].isProtected) return;
      away.push({ d, day: C.cal[i] });
    });
    away.sort((a, b) => (a.day.i - b.day.i) || cmpStr(a.d.key, b.d.key));

    /* The same collapse the crunches get, for the same reason: the side column
       is a column, and eight variations of one sentence crowd out every other
       kind of finding. */
    if (away.length > 3) {
      const first = away[0];
      out.push({
        kind: 'conflict',
        key: 'conflict:away:many',
        title: `${away.length} things are due while you are away`,
        body: `The earliest is ${first.d.title} on ${dayLabel(C, first.day.i)}, inside ${first.day.protectedBy}. `
          + 'Those days hold nothing, so all of it has to be finished before you go.',
        week: H.isoDay(H.startOfWeek(new Date(first.day.dayMs)).getTime()),
      });
    } else {
      away.forEach(({ d, day }) => {
        out.push({
          kind: 'conflict',
          key: `conflict:away:${d.key}`,
          title: `“${d.title}” is due while you are away`,
          body: `${day.protectedBy} covers ${dayLabel(C, day.i)} and this is due on it. `
            + (d.claims.length
              ? `The ${H.fmtDurMin(d.owedMin)} it needs has been claimed before you go.`
              : `The ${H.fmtDurMin(d.owedMin)} it needs has nowhere to go before you go.`),
          week: H.isoDay(H.startOfWeek(new Date(day.dayMs)).getTime()),
        });
      });
    }

    const empty = [];
    list(C.S.deadlines).forEach((d) => {
      attempt(() => {
        if (!d || !d.id || !d.due || d.due < C.NOW) return;
        if (list(H.tasksForDeadline(d.id)).some((t) => t && !t.done)) return;
        empty.push(d);
      }, null);
    });
    empty.sort((a, b) => (a.due - b.due) || cmpStr(a.id, b.id));

    if (empty.length > 3) {
      out.push({
        kind: 'conflict',
        key: 'conflict:empty:many',
        title: `${empty.length} deadlines have nothing filed against them`,
        body: `The earliest is “${empty[0].title}” on ${H.fmtDay(empty[0].due)}. No task points at any of them, so no hours have been set aside for any of them.`,
        week: weekIsoOf(empty[0].due),
      });
    } else {
      empty.forEach((d) => {
        out.push({
          kind: 'conflict',
          key: `conflict:empty:${d.id}`,
          title: `Nothing is filed against “${d.title}”`,
          body: `It is due ${H.fmtDay(d.due)} and no task points at it, so no hours have been set aside for it.`,
          week: weekIsoOf(d.due),
        });
      });
    }

    /* Opportunity — quiet only counts as an opportunity when something after it
       is short. A quiet stretch with nothing behind it is just quiet, and
       saying so would be noise. */
    for (let w = 0; w < C.weekCount; w++) {
      const surplus = x.capacity[w] - x.committed[w];
      if (surplus < 240) continue;
      let laterCrunch = -1;
      for (let v = w + 1; v < C.weekCount; v++) {
        if (x.committed[v] > x.capacity[v]) { laterCrunch = v; break; }
      }
      if (laterCrunch < 0) continue;
      const big = x.biggest[laterCrunch];
      out.push({
        kind: 'opportunity',
        key: `opportunity:${w}`,
        title: `The week of ${dayLabel(C, w * 7)} has ${H.fmtDurMin(surplus)} nobody has claimed`,
        body: `The week of ${dayLabel(C, laterCrunch * 7)} is ${H.fmtDurMin(x.committed[laterCrunch] - x.capacity[laterCrunch])} short.`
          + (big ? ` Pulling ${big.d.title} forward clears it.` : ''),
        week: H.isoDay(C.cal[w * 7].dayMs),
      });
    }

    return out
      .sort((a, b) => (SEVERITY[a.kind] - SEVERITY[b.kind]) || cmpStr(a.week || '', b.week || '') || cmpStr(a.key, b.key))
      .slice(0, 8)
      .map((i) => {
        const row = { kind: i.kind, title: i.title, body: i.body };
        if (i.week) row.week = i.week;
        if (i.action) row.action = i.action;
        return row;
      });
  }

  /* Dates already pulled out of pasted text, at the moment it was pasted.
     Sources that have since been removed take their finds with them. */
  function collectExtracted(C) {
    const lt = C.S.longterm || {};
    const live = new Set(list(lt.sources).map((s) => s && s.id).filter(Boolean));
    const out = [];
    const seen = new Set();
    list(lt.textFinds).forEach((f) => {
      if (!f || !f.sourceId || !live.has(f.sourceId)) return;
      list(f.items).forEach((x) => {
        if (!x || !x.id || !x.due || seen.has(x.id)) return;
        seen.add(x.id);
        out.push(x);
      });
    });
    return out;
  }

  /* ─────────────────── §7 the trace ─────────────────── */

  /* Named verdicts, evaluated in order, the first failure short-circuiting. A
     verdict is a structural fact rather than a weight, which is what makes the
     strip worth reading: it is the line that says nothing was silently
     dropped, and that is the thing hardest to take on trust. */

  function gatesFor(C, d) {
    const gates = [];
    const fail = (name, note) => { gates.push({ name, ok: false, note }); return gates; };

    if (d.absorbed) return fail('REAL', `counted once — also typed as “${d.absorbedBy ? (C.byKey.get(d.absorbedBy) || {}).title || d.absorbedBy : 'something else'}”`);
    gates.push({ name: 'REAL', ok: true, note: '' });

    if (d.ruled) return fail('RULED', 'you moved this yourself');
    gates.push({ name: 'RULED', ok: true, note: '' });

    if (d.pinned) {
      return fail('PINNED', `already on the clock — ${DOW[new Date(d.pinnedAt).getDay()]} ${H.fmtMinOfDay(minOfDay(d.pinnedAt))}`);
    }
    gates.push({ name: 'PINNED', ok: true, note: '' });

    if (!(d.min > 0)) return fail('PRICED', 'no estimate — counted, never scheduled');
    gates.push({ name: 'PRICED', ok: true, note: '' });

    if (d.dueMs != null && d.releaseMs > d.dueMs) return fail('RELEASED', 'cannot start before it exists');
    gates.push({ name: 'RELEASED', ok: true, note: '' });

    /* Walked rather than sliced: on a full term this is the difference between
       four hundred throwaway arrays and none. */
    const dueIdx = d.dueMs == null ? C.dayCount - 1 : H.clamp(dayIndexOf(C, d.dueMs), 0, C.dayCount - 1);
    const from = Math.max(0, dayIndexOf(C, d.releaseMs));
    let days = 0;
    let openDays = 0;
    let room = false;
    for (let i = from; i <= dueIdx && i < C.dayCount; i++) {
      const day = C.cal[i];
      days++;
      if (!day.isProtected) openDays++;
      if (!room) {
        for (let g = 0; g < day.gaps.length; g++) {
          if (day.gaps[g].to - day.gaps[g].from >= C.MIN_SESSION) { room = true; break; }
        }
      }
    }

    if (days && !openDays) return fail('UNPROTECTED', 'falls entirely inside a protected stretch');
    gates.push({ name: 'UNPROTECTED', ok: true, note: '' });

    if (!room) return fail('ROOM', 'nothing free before it fits');
    gates.push({ name: 'ROOM', ok: true, note: '' });

    const sittings = d.sessionLens.length || 1;
    gates.push({
      name: 'SPLIT',
      ok: true,
      note: sittings === 1 ? 'one sitting' : `${sittings} sittings of ${H.fmtDurMin(d.sessionLens[0] || d.owedMin)}`,
    });
    return gates;
  }

  function verdictFor(C, d) {
    if (d.verdict) return d.verdict;
    const failed = d.gates.find((g) => !g.ok);
    const kind = d.pinned ? 'PINNED'
      : failed ? failed.name
        : d.unplacedMin > 0 ? 'LATE'
          : d.spillMin > 0 ? 'OVERFLOW'
            : 'PLACED';

    let sentence;
    if (failed) sentence = failed.note;
    else if (d.unplacedMin > 0) sentence = `${H.fmtDurMin(d.unplacedMin)} of this cannot fit before ${dueLabel(d)} — it is already late`;
    else if (d.spillMin > 0) sentence = `${H.fmtDurMin(d.spillMin)} of it had nowhere to go this week`;
    else if (d.latestStartMs != null) {
      sentence = `it can begin as late as ${dayLabelOf(C, d.latestStartMs)} and still finish, with ${H.fmtDurMin(Math.max(0, d.slackMin))} spare`
        + (d.sharedWith > 0 ? ` — the same spare ${d.sharedWith} other thing${d.sharedWith === 1 ? '' : 's'} ${d.sharedWith === 1 ? 'is' : 'are'} counting on` : '');
    } else sentence = 'nothing about this had to be decided';

    return {
      kind,
      latestStartMs: d.latestStartMs,
      slackMin: d.slackMin,
      sharedWith: d.sharedWith,
      unplacedMin: d.unplacedMin,
      sentence,
    };
  }

  function attachTraces(C) {
    C.demands.concat(C.absorbed).forEach((d) => {
      d.gates = gatesFor(C, d);
      d.verdict = verdictFor(C, d);
      d.trace = {
        key: d.key,
        title: d.title,
        courseId: d.courseId,
        gates: d.gates,
        priced: { min: d.min, high: d.high, rung: d.rung, n: d.n, basis: d.basis },
        merged: d.merged,
        credited: { loggedMin: Math.round(d.loggedMin), owedMin: d.owedMin },
        capacity: d.capacityWalk,
        verdict: d.verdict,
        placed: d.placed,
      };
    });
  }

  function explain(ledger, key) {
    if (!ledger || !key) return null;
    const all = list(ledger.demands).concat(list(ledger.absorbed));
    for (let i = 0; i < all.length; i++) {
      if (all[i] && (all[i].key === key || idSafe(all[i].key) === key)) return all[i].trace || null;
    }
    return null;
  }

  /* ─────────────────── §8 reading a syllabus ─────────────────── */

  /* A PDF and a screenshot are kept as reminders and nothing is claimed about
     them. Pasted text is different: it passes through the app on its way to
     disk, which is the one moment its contents exist in memory at all, so it is
     read there and then.

     What follows is deliberately narrow. A syllabus is mostly lecture dates,
     and a parser that offers forty of them as deadlines is worse than one that
     offers nothing — so a line needs both a date and a word that makes it an
     obligation before it is offered at all. */

  const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  const MONTH_RE = '(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)';
  const DUE_WORDS = /\b(due|deadline|submit|hand\s*in|turn\s*in|by|exam|midterm|final|quiz|test|presentation|paper|essay|pset|problem\s+set|assignment|reading|post|lab)\b/i;
  const STRONG_WORDS = /\b(due|deadline|submit|exam|midterm|final)\b/i;
  const EVENT_WORDS = /\b(exam|midterm|final|quiz|test|presentation)\b/i;

  const pad2 = (n) => (n < 10 ? `0${n}` : String(n));

  function inferYear(month, dayNum, nowMs) {
    const now = new Date(nowMs);
    const lo = nowMs - 90 * 86400000;
    const hi = nowMs + 275 * 86400000;
    const options = [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1]
      .map((y) => ({ y, ms: new Date(y, month, dayNum).getTime() }))
      .filter((o) => new Date(o.ms).getMonth() === month);
    const inWindow = options.filter((o) => o.ms >= lo && o.ms <= hi);
    const pool = inWindow.length ? inWindow : options;
    pool.sort((a, b) => Math.abs(a.ms - nowMs) - Math.abs(b.ms - nowMs));
    return pool.length ? pool[0].y : now.getFullYear();
  }

  /** The date in one line, if there is one, with how much it can be trusted. */
  function dateIn(line, nowMs) {
    let m = /\b(\d{4})-(\d{2})-(\d{2})\b/.exec(line);
    if (m) {
      const y = +m[1];
      const mo = +m[2] - 1;
      const dd = +m[3];
      if (mo >= 0 && mo <= 11 && dd >= 1 && dd <= 31) {
        return { text: m[0], due: `${y}-${pad2(mo + 1)}-${pad2(dd)}`, explicitYear: true, slashed: false };
      }
    }

    m = new RegExp(`\\b${MONTH_RE}[a-z]*\\.?\\s+(\\d{1,2})(?:st|nd|rd|th)?(?:,?\\s+(\\d{4}))?\\b`, 'i').exec(line);
    if (m) {
      const mo = MONTHS.indexOf(m[1].toLowerCase());
      const dd = +m[2];
      if (mo >= 0 && dd >= 1 && dd <= 31) {
        const y = m[3] ? +m[3] : inferYear(mo, dd, nowMs);
        return { text: m[0], due: `${y}-${pad2(mo + 1)}-${pad2(dd)}`, explicitYear: !!m[3], slashed: false };
      }
    }

    m = new RegExp(`\\b(\\d{1,2})(?:st|nd|rd|th)?\\s+${MONTH_RE}[a-z]*\\.?(?:,?\\s+(\\d{4}))?\\b`, 'i').exec(line);
    if (m) {
      const dd = +m[1];
      const mo = MONTHS.indexOf(m[2].toLowerCase());
      if (mo >= 0 && dd >= 1 && dd <= 31) {
        const y = m[3] ? +m[3] : inferYear(mo, dd, nowMs);
        return { text: m[0], due: `${y}-${pad2(mo + 1)}-${pad2(dd)}`, explicitYear: !!m[3], slashed: false };
      }
    }

    /* Month-first, and refused outright when the first number cannot be a
       month. Guessing between 15/12 and 12/15 is exactly the kind of confident
       wrongness this parser exists to avoid; nothing is offered instead. */
    m = /\b(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?\b/.exec(line);
    if (m) {
      const mo = +m[1] - 1;
      const dd = +m[2];
      if (mo < 0 || mo > 11 || dd < 1 || dd > 31) return null;
      let y;
      if (m[3]) y = m[3].length === 2 ? 2000 + +m[3] : +m[3];
      else y = inferYear(mo, dd, nowMs);
      return { text: m[0], due: `${y}-${pad2(mo + 1)}-${pad2(dd)}`, explicitYear: !!m[3], slashed: true };
    }

    return null;
  }

  function titleFrom(line, dateText) {
    let t = line.replace(dateText, ' ');
    t = t.replace(/\s+/g, ' ').trim();
    const trim = () => {
      let before;
      do {
        before = t;
        t = t.replace(/^[\s\-–—:•*·|]+/, '').replace(/[\s\-–—:•*·|,;]+$/, '').trim();
        /* The due-word is a label on the line rather than part of the name of
           the thing, so a trailing one goes with the date it introduced. */
        t = t.replace(/\s*\b(due|deadline|submit|hand\s*in|turn\s*in|by|on)\b[\s:–—-]*$/i, '').trim();
      } while (t !== before);
    };
    trim();
    if (t.length < 3) t = line.replace(/\s+/g, ' ').trim();
    return t.slice(0, 80);
  }

  function extractFromText(text, opts) {
    const o = opts && typeof opts === 'object' ? opts : {};
    /* The caller's minute first, then the host's, and the wall clock only if
       this is being used before install() — which the app never does. A bare
       "March 5" cannot be dated without knowing when it is being read, so the
       reference point has to come from somewhere. */
    const nowMs = num(o.now, 0) || (H ? H.now() : new Date().getTime());
    const sourceId = str(o.sourceId);
    const courses = list(o.courses);
    const lines = String(text == null ? '' : text).split(/\r?\n/);
    const out = [];
    const seen = new Set();

    for (let i = 0; i < lines.length && out.length < 40; i++) {
      const raw = lines[i];
      const line = String(raw == null ? '' : raw).trim();
      if (!line || line.length > 300) continue;
      if (!DUE_WORDS.test(line)) continue;

      const found = dateIn(line, nowMs);
      if (!found) continue;

      const title = titleFrom(line, found.text);
      const quote = line.replace(/\s+/g, ' ').slice(0, 140);
      const kind = EVENT_WORDS.test(line) ? 'event' : 'deadline';

      /* The course two lines up counts: a syllabus names the course once and
         then lists ten dates under it. */
      const context = `${lines[i - 2] || ''}\n${lines[i - 1] || ''}\n${line}`.toLowerCase();
      let courseHint = '';
      for (let c = 0; c < courses.length; c++) {
        const name = courses[c] && courses[c].name ? String(courses[c].name) : '';
        if (name && context.indexOf(name.toLowerCase()) >= 0) { courseHint = name; break; }
      }

      const confidence = found.slashed ? 'low'
        : !found.explicitYear ? 'medium'
          : STRONG_WORDS.test(line) ? 'high' : 'medium';

      const id = `x${fnv1a32(`${sourceId}|${found.due}|${title.toLowerCase().replace(/\s+/g, ' ').trim()}`).toString(36)}`;
      if (seen.has(id)) continue;
      seen.add(id);

      out.push({
        id,
        sourceId,
        due: found.due,
        title,
        kind,
        courseHint,
        estMin: 0,            // never invented: rung four exists to refuse this
        quote,
        confidence,
      });
    }

    return out;
  }

  /* ─────────────────── §1.4 the one call app.js makes ─────────────────── */

  function build(NOW, weekStartMs) {
    const C = {
      NOW,
      weekStartMs,
      S: H.state() || {},
      SY: H.sync() || {},
      unpriced: 0,
    };
    C.lens = freshLens(Math.floor(NOW / MS_MIN));
    sessionBounds(C);
    buildCalendar(C);
    buildInventory(C);
    backwardPass(C);
    const week = makeWeek(C);
    const plan = makePlan(C);
    attachTraces(C);

    const ledger = {
      version: VERSION,
      computedAt: NOW,
      weekStart: H.isoDay(weekStartMs),
      days: C.cal,
      demands: C.demands,
      absorbed: C.absorbed,
      unpriced: C.unpriced,
      minSession: C.MIN_SESSION,
      maxSession: C.MAX_SESSION,
      weekCount: C.weekCount,
    };
    return { plan, week, ledger };
  }

  /* A planner that blanks the grid on a bad afternoon is worse than one that
     says it broke: the week tab's own stale-week guard reads WEEK.weekStart, so
     even the failure carries the right Monday and the grid still draws. */
  function recompute(opts) {
    const t0 = tick();
    const o = opts && typeof opts === 'object' ? opts : {};
    const raw = typeof o.now === 'number' ? o.now : H.now();
    const NOW = Math.floor(raw / MS_MIN) * MS_MIN;

    let weekStartMs = NOW;
    try { weekStartMs = H.startOfWeek(new Date(NOW)).getTime(); } catch (err) { weekStartMs = NOW; }

    try {
      const r = build(NOW, weekStartMs);
      return { plan: r.plan, week: r.week, ledger: r.ledger, ms: tick() - t0 };
    } catch (err) {
      if (typeof console !== 'undefined' && console.error) console.error('[engine]', err);
      const message = `The planner hit a problem: ${err && /** @type {Error} */ (err).message}`;
      return {
        plan: { status: 'error', computedAt: NOW, message, horizon: null, weeks: [], insights: [], extracted: [] },
        week: {
          status: 'error', plannedAt: NOW, message,
          weekStart: attempt(() => H.isoDay(weekStartMs), null),
          blocks: [], notes: [], days: [],
        },
        ledger: null,
        ms: tick() - t0,
      };
    }
  }

  return { install, recompute, explain, price, extractFromText, VERSION };
})();

/* The engine is a module now, and the page imports it by name rather than
   picking it up off the global. The global assignment stays because it costs a
   line and buys two things: the browser console can still poke at
   `StudyHubEngine.recompute()` when something looks wrong, and an end-to-end
   test can reach it without the page exporting a test seam it does not
   otherwise need. Nothing in the source reads it. */
if (typeof globalThis !== 'undefined') globalThis.StudyHubEngine = StudyHubEngine;

export default StudyHubEngine;
export const { install, recompute, explain, price, extractFromText, VERSION } = StudyHubEngine;
